/** @jsxImportSource ../simboluri-jsx */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PAL_LAYERE, PAL_LAYERE_3D, PAL_TEREN, PAL_SAPATURA } from "../paletaDesen";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { scaraImplicita, treptele } from "../scari3d";
import { axaGrinzii } from "../axaGrinzii";
import { FemPanou, FemResult } from "../tipuri";
import { t } from "../../i18n";
import { grupTerenSapatura, titluTerenSapatura } from "../teren3d";
import { grupCote } from "../cote3d";
import { elibereazaCote, grupCoteObiect } from "../coteObiect3d";
import Layere3D from "../Layere3D";
import { Layer3D, aruncaUmbra, fundalClasic, fundalRealist, layerDeAfisat, layerSalvat, luminiRealist, materialRealist, mediuRealist, muchiiFamilie, salveazaLayer } from "../realist3d";
import { ArmaturaRezumat, Evidentiere, GrupArmatura, construiesteArmatura } from "../armatura3d";
import { amprentaFaraPereti, amprentaStalp } from "../amprente3d";
import Evidentiere3D from "../Evidentiere3D";
import { getArmatura3d } from "../apiStructura";
import { cutiaFizica, incadreazaCutia, orienteazaCote, scaleazaHud } from "../hud3d";
import { legComenzi3D, primitiveOrbita, schemaMouse } from "../../comenzi3d";
import BaraSectiune from "../BaraSectiune";
import { AxaSectiune, Limite, StareSectiune, creeazaSectiune, inversPentruCamera, laTaietura, limiteGrila, planTaiere, seVedeDupaTaiere } from "../sectiune3d";
import { CULORI, CULORI_CSS, PAS_GRILA_M, fundareaDesenata, goluriFlat, intinderea, nr, nucleeFlat, parapeteFlat, placaPeSolDesenata, radiereDesenate } from "./model";
import type { Selectie } from "./model";

export function Scene3D({
  r,
  sel,
  onSel,
  noduri,
  projectId,
  cloneId,
  versiune,
  straturiIn,
  faraArmare,
  onDeschide,
}: {
  r: FemResult;
  sel: Selectie;
  onSel: (s: Selectie) => void;
  // dublu-click pe o piesă: deschide-i pagina. Lipsește acolo unde scena n-are
  // unde trimite (pagina Pasului 7 își ține panoul lângă ea).
  onDeschide?: (s: Selectie) => void;
  // MENIUL STUDIOULUI: când e dat, straturile nu mai plutesc peste scenă, ci
  // se scriu în grupul «Afișare» al meniului din stânga, ca pe arhitectură și
  // pe planul de situație — comutatoare cu nume, cele aprinse din start la
  // vedere și restul sub «Mai multe straturi». Pe pagina Pasului 7 lipsește:
  // acolo panoul rămâne cel de până acum.
  straturiIn?: HTMLElement | null;
  // de unde se cere armătura layerului «Realist» și pe ce proiect se ține
  // minte layerul ales. Lipsesc acolo unde scena e doar o vitrină (v.
  // vizualizatorul de soluții): atunci se desenează betonul, fără armătură.
  projectId?: string;
  cloneId?: string | null;
  versiune?: string | null;
  // nodurile FEM aprinse din start: DOAR pe pagina Pasului 7, unde modelul de
  // calcul e subiectul. Pe suprafața de prezentare (Studio) scena arată numai
  // materie — bulinele sunt NOTAȚIE, iar o notație peste clădire pune exact
  // întrebarea „ce e asta, ceva real?"
  noduri?: boolean;
  // rândul privit n-are niciun pas de armare rulat (o schiță, o macro
  // înaintea detaliului): layerul realist n-are armătură de arătat, deci
  // scena pornește în «Clasic»
  faraArmare?: boolean;
}) {
  const mount = useRef<HTMLDivElement>(null);
  const api = useRef<{
    render: () => void;
    highlight: (s: Selectie) => void;
    setVis: (key: string, on: boolean) => void;
    setArmatura: (a: ArmaturaRezumat | null) => void;
    evidentiaza: (ev: Evidentiere) => void;
    setSectiune: (s: StareSectiune | null) => void;
  } | null>(null);
  // LAYERUL: felul randării. «Realist» e implicit — betonul e translucid cu
  // muchia trasată, iar armătura, care e tot un rezultat al soluției, se
  // vede în el. «Clasic» rămâne o apăsare distanță, cu tot ce e pe ecran
  // neatins: alegerea se ține pe proiect, în browserul omului.
  //
  // FĂRĂ ARMARE, implicitul e «Clasic»: realistul ar arăta același beton,
  // fără nimic înăuntru. Implicitul nu SCRIE alegerea proiectului — pe o
  // soluție armată se revine la ea —, iar ce alege omul în scena asta rămâne
  // al lui, cu armare sau fără.
  const salvat = useMemo(() => layerSalvat(projectId ?? "-"), [projectId]);
  const [ales, setAles] = useState<Layer3D | null>(null);
  const layer = layerDeAfisat(ales, salvat, faraArmare);
  const setLayer = (l: Layer3D) => {
    setAles(l);
    if (projectId) salveazaLayer(projectId, l);
    // evidențierea e a layerului realist: în «Clasic» n-are panou, deci
    // n-ar mai avea nici cum să fie stinsă
    if (l !== "realist") setEvid(null);
  };
  const [armatura, setArmatura] = useState<ArmaturaRezumat | null>(null);
  // EVIDENȚIEREA: ce TIP de componentă e aprins în aur (v. `Evidentiere3D`).
  // Stare a vederii, ca secțiunea: nu schimbă ce e în model, doar ce sare
  // în ochi.
  const [evid, setEvid] = useState<Evidentiere>(null);
  const evidRef = useRef(evid);
  evidRef.current = evid;
  const camRef = useRef<{ poz: THREE.Vector3; tinta: THREE.Vector3 } | null>(
    null);
  // SECȚIUNEA: o stare a VEDERII, nu un layer — de asta stă lângă `vis`, nu
  // în el, și de asta bara ei stă lângă comutatorul de randare, nu în lista
  // de bife. `null` = nimeni nu taie nimic, adică scena de până acum, bit cu
  // bit: fără plan de tăiere, `renderer.clippingPlanes` e gol și nimic din
  // ce urmează nu se construiește.
  const [sect, setSect] = useState<StareSectiune | null>(null);
  const sectRef = useRef(sect);
  sectRef.current = sect;
  // capetele cursorului: se măsoară pe scena construită (v. `limiteGrila`),
  // deci ajung în React abia după primul efect
  const [limite, setLimite] = useState<Limite | null>(null);
  // Ce e aprins la deschiderea scenei. Tot de aici se hotărăște și ce stă la
  // vedere în meniul studioului: un strat aprins din start are comutatorul
  // afară, nu sub «Mai multe straturi».
  const visImplicit = () => ({
    // stâlpii și grinzile au și ele comutator: fără ele
    // nu mai e model, dar tocmai de asta stau în fața a ce e dedesubt — al
    // fundației, al săpăturii, al cotelor
    stalpi: true,
    grinzi: true,
    placa: true,
    // ARMĂTURA: numai în layerul «Realist», unde e desenată. Aprinsă
    // implicit — e motivul pentru care există layerul —, dar are rândul ei,
    // ca să poată fi stinsă fără să pierzi modul.
    armatura: true,
    // rețeaua de elemente finite a plăcii: STINSĂ implicit, ca celelalte
    // notații. E discretizarea de calcul, nu materie — și e desenată din linii
    // de un pixel, dese cât ochiurile: privite razant ele cad sub un pixel și
    // clipesc de la un cadru la altul, adică pun peste clădire exact zgomotul
    // pe care restul scenei îl evită. Se aprinde când întrebarea e „din ce e
    // făcut fiecare ochi".
    mesh: false,
    noduri: !!noduri,
    cm: true,
    pereti: true, // zidăria e implicit VIZIBILĂ: de când
                  // are id-uri și e clicabilă, e prima la care te uiți ca să
                  // verifici ce a văzut Pasul 2 pe planuri
    peretiBeton: true, // pereții de beton ai subsolului — structură, vizibili
    zidarieConfinata: true, // pereții structurali din zidărie ai proeminenței
    // BALCOANELE arhitecturii (conturul desenat de arhitect, cu fațetele
    // planului de scurgere): STINSE implicit — pagina e a modelului, iar
    // placa în consolă a modelului se vede oricum, ca placă. Se aprind când
    // întrebarea e „ce balcon a citit importul".
    balcoane: false,
    // fundația: betonul e VIZIBIL implicit — face parte din structură. Când
    // ea NU e în modelul de calcul (bază încastrată) se desenează tot, dar
    // stinsă: pagina e a modelului, iar betonul ăla nu e în el.
    fundatie: (r.model.grinzi_fundare ?? []).length > 0
      || radiereDesenate(r.model).length > 0,
    // placa pe sol: se toarnă, deci se vede; e gri fiindcă nu ține structura
    placaSol: true,
    // ARHITECTURA importată — ferestrele și ușile STINSE implicit: pagina e
    // a modelului de calcul, iar golurile sunt context. Se aprind când
    // întrebarea e „ce a văzut importul pe planuri". Parapetele stau
    // aprinse: fără ele un element de pe consolă pare în aer.
    ferestre: false,
    usi: false,
    parapete: true,
    // nucleul (casele de scară, lifturile): APRINS — structuristul își pune
    // pereții mari în jurul lui, deci e primul lucru pe care îl caută în scenă
    nuclee: true,
    // COTELE: câteva cote importante — laturile, înălțimile de etaj, cotele
    // de nivel. Stinse implicit: sunt notație, nu materie.
    cote: false,
    // TERENUL și SĂPĂTURA — stinse implicit. Nu sunt
    // structură: sunt pământul din jur și golul din care s-a scos. Se aprind
    // când întrebarea e „cât se sapă și în ce teren".
    terenSapatura: false,
  });
  const [vis, setVis] = useState(visImplicit);
  // scena se reconstruiește când vine un rezultat nou (rerularea pasului);
  // atunci layerele bifate și selecția trebuie reaplicate pe scena nouă,
  // altfel bifa rămâne pusă dar layerul e stins
  const visRef = useRef(vis);
  visRef.current = vis;
  // «doar X»: numai layerul cerut rămâne aprins; «toate»/«nimic»: tot panoul
  // pe o singură stare. Cheile care nu au rând (elemente absente din model)
  // urmează aceeași soartă — n-au ce arăta oricum.
  const setDoar = (cheie: keyof typeof vis) =>
    setVis((v) => Object.fromEntries(
      Object.keys(v).map((k) => [k, k === cheie])) as typeof v);
  const setToate = (on: boolean) =>
    setVis((v) => Object.fromEntries(
      Object.keys(v).map((k) => [k, on])) as typeof v);
  const selRef = useRef(sel);
  selRef.current = sel;
  const onDeschideRef = useRef(onDeschide);
  onDeschideRef.current = onDeschide;

  useEffect(() => {
    const el = mount.current;
    if (!el) return;

    const model = r.model;
    const nd = new Map(model.noduri.map((n) => [n.id, n]));
    // Cutia nodurilor, dintr-o singură trecere: un model cu zeci de mii de
    // noduri nu încape ca argumente într-un `Math.min(...xs)` — stiva de
    // apel are un plafon, iar depășirea lui omoară scena întreagă.
    const ix = intinderea(model.noduri.map((n) => n.x_m));
    const iy = intinderea(model.noduri.map((n) => n.y_m));
    const iz = intinderea(model.noduri.map((n) => n.z_m));
    const cx = (ix.min + ix.max) / 2;
    const cy = (iy.min + iy.max) / 2;
    const cz = (iz.min + iz.max) / 2;
    const span = Math.max(ix.max - ix.min, iy.max - iy.min, iz.max - iz.min);

    const realist = layer === "realist";
    const scene = new THREE.Scene();
    const fundalTex = realist ? fundalRealist() : fundalClasic();
    scene.background = fundalTex;
    // Lumina e NEUTRĂ. O lumină colorată vopsește tot modelul cu nuanța ei,
    // peste paletă: cu un soare gălbui și o ambianță crem, albastrul cadrului
    // vira spre verde-cenușiu, iar întreaga scenă căpăta un ton de fotografie
    // veche. Convenția paletei (v. `paletaDesen.ts`) cere ca albastrul să fie
    // albastru și roșul roșu, deci lumina nu are voie să aibă culoare a ei.
    //
    // Ambianța e RIDICATĂ, nu un `HemisphereLight`: acela dă culoarea cerului
    // doar fețelor orientate în sus, iar un panou vertical ar primi jumătate.
    // Peste ea, o direcțională slabă care doar modelează colțurile, plus un
    // fill dinspre observator, ca fața spre cameră să nu fie cea umbrită.
    //
    // INTENSITĂȚILE SUNT FIZICE, NU PROCENTE. three nu mai are
    // `useLegacyLights`: difuza împarte iradianța la π, deci o ambianță de
    // intensitate `I` ajunge pe o față neluminată ca `I/π` din culoarea ei.
    // Cu `I` sub 1, clădirea s-ar desena la o treime din paleta ei — o pată
    // închisă pe un fundal deschis, în care orice culoare potolită iese
    // noroioasă în loc de discretă. Cu numerele de mai jos, fața umbrită stă
    // la ~0,64 din culoarea ei și cea luminată la ~1,0: exact banda în care a
    // fost aleasă paleta (v. `paletaDesen.ts`).
    if (!realist) {
      scene.add(new THREE.AmbientLight(0xffffff, 2.15));
      const dir = new THREE.DirectionalLight(0xffffff, 1.0);
      dir.position.set(1.5 * span, 0.8 * span, 2 * span);
      scene.add(dir);
      const fill = new THREE.DirectionalLight(0xffffff, 0.75);
      fill.position.set(-1.2 * span, -1.4 * span, 0.9 * span);
      scene.add(fill);
    }

    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, span * 20);
    camera.up.set(0, 0, 1);
    camera.position.set(cx + span * 0.9, cy - span * 1.1, cz + span * 0.75);

    // preserveDrawingBuffer: canvas-ul rămâne citibil (screenshot/export).
    // stencil: three nu mai cere buffer de stencil din oficiu (r163), iar fără
    // el SECȚIUNEA n-ar avea cu ce umple tăietura — capacul se croiește pe
    // stencil (v. `sectiune3d.ts`) și, fără buffer, s-ar desena peste tot,
    // adică o pată de beton cât ecranul în locul feței tăiate.
    const renderer = new THREE.WebGLRenderer({
      antialias: true, preserveDrawingBuffer: true, stencil: true,
    });
    // Pe un ecran Retina, `devicePixelRatio` 2 înseamnă de patru ori mai
    // mulți pixeli de umplut. Layerul realist desenează prin cinci-șase
    // straturi translucide pentru fiecare pixel, deci acolo plafonul chiar
    // se simte: 1,5 e limita sub care muchiile încă nu se zimțesc.
    renderer.setPixelRatio(Math.min(window.devicePixelRatio,
                                    realist ? 1.5 : 2));
    el.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    // ACELEAȘI COMENZI CA PE CELELALTE VEDERI: schema de mouse, rotița,
    // gesturile de trackpad și tastele stau într-un singur loc
    // (`comenzi3d.ts`), fiindcă omul trece între modelul de structură și
    // machetele arhitecturii din butoane alăturate — același gest n-are voie
    // să facă acolo altceva. Legarea propriu-zisă e mai jos, după ce scena e
    // construită: încadrarea de la tasta «0» are nevoie de ea.
    schemaMouse(controls, true);
    controls.target.set(cx, cy, cz);
    // Schimbarea layerului reconstruiește scena, dar NU trebuie să mute
    // camera: omul se uită la un colț anume și vrea să-l vadă altfel, nu
    // altundeva. Poziția și ținta se iau de la scena de dinainte.
    if (camRef.current) {
      camera.position.copy(camRef.current.poz);
      controls.target.copy(camRef.current.tinta);
    }
    controls.update(); // altfel camera rămâne orientată spre origine
    const tineMinteCamera = () => {
      camRef.current = { poz: camera.position.clone(),
                         tinta: controls.target.clone() };
    };
    controls.addEventListener("change", tineMinteCamera);
    // …și o dată acum: sensul implicit al secțiunii se alege din direcția de
    // privire, iar ea trebuie știută înainte ca omul să fi mișcat camera
    tineMinteCamera();

    // --- CINE CÂȘTIGĂ ÎNTÂLNIREA A DOUĂ FEȚE --------------------------------
    // Fețele scenei nu se ating din întâmplare, ci prin construcție: fața unui
    // stâlp e în ACELAȘI plan cu fața zidului în care stă (stâlpul se toarnă în
    // grosimea peretelui), cutia unei ferestre stă chiar în grosimea zidului,
    // iar inima grinzii intră în stâlpul de la capăt. Două fețe la aceeași
    // adâncime nu au un câștigător: îl decid erorile de rotunjire ale
    // proiecției, care se schimbă cu unghiul. De acolo pâlpâirea — pete care se
    // aprind și se sting când întorci clădirea, cel mai vizibil la ferestre și
    // la zidurile tăiate de stâlpi.
    //
    // Nu se repară mutând geometria (ea e corectă: peretele CHIAR e acolo), ci
    // spunând o dată, pentru toată scena, cine iese deasupra la egalitate:
    //
    //   1. STRUCTURA BATE ÎNVELIȘUL. Zidăria, golurile, parapetele, placa pe
    //      sol și betonul fundației primesc `polygonOffset` pozitiv: la
    //      adâncime egală se dau înapoi. Câștigătorul e mereu același, nu tras
    //      la sorți în fiecare cadru.
    //   2. TRANSLUCIDUL NU SCRIE ADÂNCIME. `depthWrite: false` scoate din joc
    //      și fața din spate a aceleiași cutii (care se bătea cu fața din față,
    //      de unde petele pe ziduri), și pereții care se acopereau unul pe
    //      altul.
    //   3. ORDINEA E FIXĂ, NU A DISTANȚEI. A doua cauză, alta decât adâncimea,
    //      și cea care dă PETELE MARI: three sortează translucidele după
    //      centrul lor, la fiecare cadru. O rază care traversează clădirea
    //      trece prin patru-cinci ziduri; sunt de aceeași culoare, dar
    //      `MeshLambert` le umbrește diferit, după cum e orientat fiecare,
    //      deci amestecul lor DEPINDE de ordine — iar ordinea se răstoarnă
    //      între două ziduri aproape la fel de departe, la cea mai mică
    //      rotire. Leacul: fiecare piesă primește `renderOrder`-ul ei, dat la
    //      construcție (familia × 100000 + rândul în familie). Amestecul nu
    //      mai e cel „din spate în față", recalculat mereu — e ACELAȘI în
    //      toate cadrele, iar între ziduri de aceeași culoare diferența nu se
    //      vede. Între familii ordinea rămâne cea firească: zidul întâi,
    //      fereastra din el peste el.
    //
    // Scara cedării, de la ce ține clădirea spre ce o îmbracă. UN SINGUR
    // număr per familie, și toate DIFERITE: două familii pe aceeași treaptă
    // sunt din nou la egalitate, adică din nou pâlpâire. Stâlpul n-are treaptă
    // — el e reperul, câștigă tot; grinda intră în el, panoul de beton se țese
    // cu el, fundația îl primește, învelișul stă peste toate.
    const ORD = { stalp: 1, grinda: 2, pereteBeton: 3, fundatie: 4,
                  placa: 5, mesh: 6, invelis: 7, balcon: 8, parapet: 9, gol: 10,
                  zidarieConfinata: 11 };
    const CEDEAZA = { grinda: 1, pereteBeton: 2, fundatie: 3, invelis: 4,
                      zidarieConfinata: 5 };
    /** Materialul unei piese care se dă înapoi la adâncime egală. */
    const cedeaza = <T extends THREE.Material>(m: T, treapta: number): T => {
      m.polygonOffset = true;
      m.polygonOffsetFactor = treapta;
      m.polygonOffsetUnits = treapta;
      return m;
    };
    /** …și, dacă e și translucid, nu scrie adâncime. */
    const invelis = <T extends THREE.Material>(
      m: T, treapta = CEDEAZA.invelis): T => {
      cedeaza(m, treapta);
      m.depthWrite = false;
      return m;
    };
    /** Ordinea de desenare a unui layer întreg. `renderOrder` NU se
     *  moștenește de la grup în three, deci se pune pe fiecare piesă — și
     *  DISTINCT, ca sortarea pe distanță să nu mai aibă ce răsturna.
     *
     *  Se aplică ȘI familiilor OPACE, nu doar celor translucide. Scara
     *  cedării desparte familii diferite, dar nu două piese din ACEEAȘI
     *  familie: la fiecare intersecție de grinzi, fețele lor de sus sunt în
     *  același plan și au aceeași treaptă, iar la adâncime egală câștigă cine
     *  a fost desenat primul — adică, fără regula asta, cine e mai aproape de
     *  cameră în cadrul curent. */
    const asaza = (grup: THREE.Object3D, ordine: number) => {
      let k = 0;
      grup.traverse((o) => { o.renderOrder = ordine * 100000 + k++; });
    };

    const render = () => {
      // etichetele (straturi, marcaje) NU se micșorează cu distanța: mărimea
      // lor e în pixeli, recalculată în metri la fiecare cadru; cotele stau
      // în planul lor și doar se întorc ca să se citească
      scaleazaHud(scene, camera, renderer.domElement.clientHeight || 560);
      orienteazaCote(scene, camera);
      renderer.render(scene, camera);
    };
    controls.addEventListener("change", render);

    // --- elementele structurale (mesh-uri clicabile) -------------------------
    const pickables: THREE.Mesh[] = [];
    // BETONUL, pe layere. În «Clasic» fiecare familie are culoarea ei (v.
    // convenția din `paletaDesen`); în «Realist» toate au același material
    // de sticlă, iar ce le deosebește sunt muchiile trasate și armătura din
    // ele. Selecția rămâne aurul paletei în amândouă.
    const matStalp = realist
      ? invelis(materialRealist("stalp"), 0)
      : new THREE.MeshLambertMaterial({ color: CULORI.stalp });
    const matGrinda = realist
      ? invelis(materialRealist("grinda"), CEDEAZA.grinda)
      : cedeaza(new THREE.MeshLambertMaterial({ color: CULORI.grinda }),
                CEDEAZA.grinda);
    // piesa aleasă IESE ÎN FAȚĂ: treapta negativă o scoate deasupra a tot ce o
    // atinge, ca aurul selecției să nu poată fi acoperit chiar de zidul din
    // care tocmai ai ales-o
    const matSel = cedeaza(new THREE.MeshLambertMaterial({
      color: CULORI.selectat,
      emissive: 0x6b4a10,
    }), -1);

    // FĂRĂ etichete plutitoare în scenă: ID-ul oricărui
    // element (stâlp SAU grindă) se vede în panoul de proprietăți deschis la
    // click — etichetele pe desen aglomerau scena.
    const gStalpi = new THREE.Group();
    const gGrinzi = new THREE.Group();
    scene.add(gStalpi);
    scene.add(gGrinzi);
    // ordinea lor se așază la sfârșit, după ce grupurile s-au umplut

    model.stalpi.forEach((s, i) => {
      const a = nd.get(s.nod_jos)!;
      const b = nd.get(s.nod_sus)!;
      const L = b.z_m - a.z_m;
      // Orientarea secțiunii: `h_m` e latura de-a lungul lui `dir` (direcția
      // peretelui), `b_m` cea transversală — aceeași convenție ca în
      // Comparatie.tsx. Înainte cutia se construia (b_m, h_m, L) și NU se
      // rotea deloc: pe o clădire rotită (aici 16° — pereții la 74°/164°)
      // fiecare stâlp alungit apărea strâmb și ieșit din zid în 3D, deși în
      // plan era corect. ST13.1 (35×98 cm) era desenat cu latura de 98 cm pe
      // axa globală Y, adică la ~106° de peretele lui.
      // Stâlpul în L/T are amprenta reală din Pasul 4: se extrudează ea.
      // Cutia dreptunghiului echivalent (piciorul dominant) ieșea din zid
      // cu ~40 % la un L de 2,5 m, deși în plan stâlpul stă întreg în pereți.
      const extruda = (amp: [number, number][]) => new THREE.ExtrudeGeometry(
        new THREE.Shape(amp.map(([x, y]) => new THREE.Vector2(x - a.x_m, y - a.y_m))),
        { depth: L, bevelEnabled: false });
      // Capătul care intră într-un perete de beton continuu se oprește la
      // fața peretelui: grosimea lui e a peretelui, iar lamelele care se
      // opresc în el ies toate la fel — niciuna pe fața lui de dincolo (v.
      // `amprentaFaraPereti`). Cotele măsoară însă stâlpul ÎNTREG: lungimea
      // lui e cea din model, nu doar cât se vede din el.
      const taiata = amprentaFaraPereti(s, amprentaStalp(s, a),
        model.pereti_beton ?? []);
      let mesh: THREE.Mesh;
      let geoCote: THREE.BufferGeometry | undefined;
      if (taiata) {
        mesh = new THREE.Mesh(extruda(taiata), matStalp);
        mesh.position.set(a.x_m, a.y_m, a.z_m);
        geoCote = extruda(amprentaStalp(s, a));
      } else if (s.amprenta_m && s.amprenta_m.length >= 3) {
        mesh = new THREE.Mesh(extruda(s.amprenta_m), matStalp);
        mesh.position.set(a.x_m, a.y_m, a.z_m);
      } else {
        const geo = new THREE.BoxGeometry(s.h_m, s.b_m, L);
        mesh = new THREE.Mesh(geo, matStalp);
        mesh.position.set(a.x_m, a.y_m, a.z_m + L / 2);
        if (s.dir) mesh.rotation.z = Math.atan2(s.dir[1], s.dir[0]);
      }
      // `unghi` e direcția piesei în plan, pentru cotele ei (v.
      // `coteObiect3d`): pe ramura cu amprentă mesh-ul NU e rotit — forma e
      // deja în coordonatele lumii —, deci unghiul nu se poate citi înapoi
      // din `rotation.z`, dar el e tot al peretelui.
      mesh.userData = {
        tip: "stalp", ref: i,
        unghi: s.dir ? Math.atan2(s.dir[1], s.dir[0]) : 0,
        ...(geoCote ? { geoCote } : {}),
      };
      gStalpi.add(mesh);
      pickables.push(mesh);
    });

    // BRAȚUL RIGID: de la nodul stâlpului la capătul real al grinzii, o linie
    // subțire întreruptă în culoarea grinzii — e al ei, dar nu e grindă.
    // Se vede prin ce o acoperă (`depthTest: false`): brațul scurt stă în
    // betonul stâlpului, cel lung în zidul din care pleacă, și tocmai pe ăla
    // trebuie să-l citești ca braț, nu ca o grindă oblică lipsă.
    const matBrat = new THREE.LineDashedMaterial({
      color: CULORI.grinda, dashSize: 0.2, gapSize: 0.12,
      depthTest: false, transparent: true, opacity: 0.9,
    });
    model.grinzi.forEach((g, i) => {
      const a = nd.get(g.nod_i)!;
      const b = nd.get(g.nod_j)!;
      // AXA REALĂ a grinzii = nodul + offsetul de capăt (zona rigidă din
      // stâlp), v. `axaGrinzii`. Desenată nod-la-nod, grinda stâlpului în
      // L/T ieșea oblică, tăind zidurile (FEM-ul folosea deja axa reală
      // prin jntOffset).
      const axa = axaGrinzii(g, a, b);
      const [ax, ay] = axa.a;
      const [bx, by] = axa.b;
      const { L } = axa;
      if (L < 1e-6) return;
      // GRINDA = INIMA de sub placă, nu toată înălțimea T-ului. Nodul stă în
      // planul MEDIAN al plăcii, deci fața de jos a plăcii e la z − hf/2 și
      // de acolo atârnă inima. Desenat cu `h_m` (înălțimea TOTALĂ, cu tot cu
      // placa) și fața de sus la cota plăcii, grinda ar ieși
      // 5,5 cm peste umbra plăcii, iar acum că placa are volum ar fi umplut-o
      // a doua oară cu betonul ei («poziția corect
      // reprezentată în relație cu grinzile și stâlpii»). Legenda spunea
      // „inimă sub placă" de mult — desenul nu.
      const hInima = g.h_web_m ?? Math.max(g.h_m - g.hf_m, 0.05);
      const geo = new THREE.BoxGeometry(L, g.bw_m, hInima);
      const mesh = new THREE.Mesh(geo, matGrinda);
      const zAxa = a.z_m - g.hf_m / 2 - hInima / 2;
      mesh.position.set((ax + bx) / 2, (ay + by) / 2, zAxa);
      mesh.rotation.z = axa.unghi;
      mesh.userData = { tip: "grinda", ref: i, unghi: axa.unghi };
      gGrinzi.add(mesh);
      pickables.push(mesh);
      // brațele, la cota axei grinzii; intră în grupul grinzilor, deci se
      // sting odată cu ele; nu se aleg cu clickul — grinda e piesa
      axa.brate.forEach((br) => {
        const linie = new THREE.Line(
          new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(br.nod[0], br.nod[1], zAxa),
            new THREE.Vector3(br.cap[0], br.cap[1], zAxa),
          ]), matBrat);
        linie.computeLineDistances();
        linie.userData = { brat: true, ref: i };
        gGrinzi.add(linie);
      });
    });

    // --- placa (diafragma), conturul + umbra translucida ----------------------
    const gPlaca = new THREE.Group();
    // FrontSide, nu DoubleSide: de când placa e un VOLUM închis, fața din
    // spate a fiecărei cutii n-are ce fi văzută, iar desenată tot ar fi —
    // suprapusă peste cea din față, cu 0,13 peste 0,13, ochiul de placă ar
    // ieși de două ori mai opac decât umbra dinainte. `depthWrite: false`
    // rămâne: structura se vede prin placă.
    const matPlaca = realist
      ? invelis(materialRealist("placa"))
      : invelis(new THREE.MeshBasicMaterial({
        color: CULORI.placa,
        transparent: true,
        opacity: 0.13,
        side: THREE.FrontSide,
      }));
    const matContur = new THREE.LineBasicMaterial({ color: CULORI.placa });
    // ochiul ALES iese în față, ca piesa aleasă (`matSel`): treapta
    // negativă și ordinea de desenare a selecției (v. `highlight`) îl scot
    // deasupra terasei sau balconului care stă pe el, iar opacitatea mare îl
    // face să se citească drept UN ochi, nu ca o pată peste placă
    const matPanouSel = invelis(new THREE.MeshBasicMaterial({
      color: CULORI.selectat,
      transparent: true,
      opacity: 0.78,
      side: THREE.FrontSide,
    }), -1);
    // Placa se desenează PE OCHIURI, nu ca o pată pe tot nivelul («placa ar
    // trebui desenată doar în ochiuri»). Un singur
    // poligon pe nivel, cu 247 de puncte care urmăreau conturul zidurilor,
    // arăta ca desenat în jurul grinzilor. Fiecare ochi e clicabil, ca
    // stâlpii și grinzile.
    //
    // …și are GROSIME. O suprafață de grosime zero pusă la z + 2 cm — o cotă
    // inventată, nici fața de sus, nici cea de jos
    // a plăcii —, așa că nu se putea vedea nici cât e groasă placa, nici cum
    // se așază peste inima grinzii («placa trebuie să
    // aibă volumul corect reprezentat la toate etajele și poziția corect
    // reprezentată în relație cu grinzile și stâlpii»).
    //
    // CONVENȚIA, o singură dată aici: nodul de diafragmă e în planul MEDIAN
    // al plăcii (de asta grinzile au offsetul compozit −hf/2 și de asta
    // panourile de subsol coboară cu h_ocupat − t/2). Deci placa nivelului
    // ocupă [z − t/2, z + t/2]: fața ei de SUS e pardoseala nivelului de
    // deasupra, fața de JOS e tavanul, de unde pornește inima grinzii.
    const tPl = model.grosime_placa_m ?? 0.15;
    const zNivel = (niv: number) =>
      model.etaje.find((e) => e.nivel_diafragma === niv)?.z_m ?? 0;
    // forma unui ochi, cu golurile lui decupate: inelele interioare și
    // zonele de gol (casa scării, liftul) sunt gaură în placă, deci nici în
    // volumul desenat n-au beton — aceeași regulă ca în Ochi3D
    const formaOchi = (p: FemPanou) => {
      const shape = new THREE.Shape(
        p.contur.map(([x, y]) => new THREE.Vector2(x, y))
      );
      [
        ...(p.inele ?? []).map((inel) => inel.contur),
        ...(p.zone_gol ?? []).map((z) => z.contur),
      ]
        .filter((g): g is [number, number][] => (g?.length ?? 0) >= 3)
        .forEach((g) => {
          shape.holes.push(
            new THREE.Path(g.map(([x, y]) => new THREE.Vector2(x, y)))
          );
        });
      return shape;
    };
    const panouri = model.panouri ?? [];
    if (panouri.length) {
      panouri.forEach((p, i) => {
        if (!p.contur || p.contur.length < 3) return;
        const zSus = zNivel(p.nivel) + tPl / 2;
        const mesh = new THREE.Mesh(
          new THREE.ExtrudeGeometry(formaOchi(p), {
            depth: tPl,
            bevelEnabled: false,
          }),
          matPlaca
        );
        mesh.position.z = zSus - tPl;
        mesh.userData = { tip: "panou", ref: i };
        gPlaca.add(mesh);
        pickables.push(mesh);
        const pts = [...p.contur, p.contur[0]].map(
          ([x, y]) => new THREE.Vector3(x, y, zSus)
        );
        gPlaca.add(
          new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), matContur)
        );
      });
    }
    // conturul planșeului rămâne desenat (linia exterioară), ca să se vadă
    // dacă ochiurile acoperă tot nivelul sau lipsește placă pe undeva
    model.etaje.forEach((et) => {
      const zSus = et.z_m + tPl / 2;
      et.slab.forEach((rings) => {
        if (!panouri.length) {
          const shape = new THREE.Shape(
            rings[0].map(([x, y]) => new THREE.Vector2(x, y))
          );
          rings.slice(1).forEach((hole) => {
            shape.holes.push(new THREE.Path(hole.map(([x, y]) => new THREE.Vector2(x, y))));
          });
          const mesh = new THREE.Mesh(
            new THREE.ExtrudeGeometry(shape, { depth: tPl, bevelEnabled: false }),
            matPlaca
          );
          mesh.position.z = zSus - tPl;
          gPlaca.add(mesh);
        }
        rings.forEach((ring) => {
          const pts = ring.map(([x, y]) => new THREE.Vector3(x, y, zSus));
          gPlaca.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), matContur));
        });
      });
    });
    asaza(gPlaca, ORD.placa);
    scene.add(gPlaca);

    // --- pereții de ZIDĂRIE (gri, implicit ascunși) — clicabili --------------
    // fiecare perete simplificat al Pasului 2 e o piesă cu id-ul lui (S7,
    // E14…), ca să poată fi identificat în discuție; `iZid` numără în
    // ACEEAȘI ordine ca zidarieFlat()
    const gPereti = new THREE.Group();
    // FrontSide, nu DoubleSide: extrudarea e un corp ÎNCHIS, deci fața din
    // spate a fiecărui zid n-are ce fi văzută — desenată, se bătea cu cea din
    // față exact la aceeași adâncime.
    const matPereti = realist
      ? invelis(materialRealist("zid"))
      : invelis(new THREE.MeshLambertMaterial({
        color: CULORI.zidarie,
        // destul cât să se vadă unde e zid, nu atât cât să ascundă cadrul
        // din spatele lui: zidăria e context, structura e subiectul
        opacity: 0.42,
        transparent: true,
        side: THREE.FrontSide,
      }));
    let iZid = 0;
    model.etaje.forEach((et) => {
      // înălțimea reală a etajului (Pasul 2 → Pasul 4); media doar ca rezervă
      const hEtaj = et.inaltime_m ?? r.model.inaltime_etaj_m ?? 3;
      (et.pereti ?? []).forEach((w) => {
        const inele = w.inele ?? [];
        if (inele.length === 0 || inele[0].length < 3) { iZid++; return; }
        const forma = (rings: [number, number][][]) => {
          const shape = new THREE.Shape(rings[0].map(([x, y]) => new THREE.Vector2(x, y)));
          rings.slice(1).forEach((hole) => {
            shape.holes.push(new THREE.Path(hole.map(([x, y]) => new THREE.Vector2(x, y))));
          });
          return shape;
        };
        // DIRECȚIA peretelui de zidărie, pentru cotele lui (v.
        // `coteObiect3d`): el nu vine cu un `dir`, ca stâlpul și grinda, ci cu
        // un contur — e desenat din inelele arhitecturii, în coordonatele
        // lumii, deci mesh-ul nici nu e rotit. Direcția e a LATURII CELEI MAI
        // LUNGI a conturului: la un perete, ea e lungimea, iar celelalte
        // laturi sunt grosimea și capetele.
        let unghiZid = 0;
        let ceaMaiLunga = 0;
        inele[0].forEach(([ax, ay], k) => {
          const [bx, by] = inele[0][(k + 1) % inele[0].length];
          const l = Math.hypot(bx - ax, by - ay);
          if (l > ceaMaiLunga) {
            ceaMaiLunga = l;
            unghiZid = Math.atan2(by - ay, bx - ax);
          }
        });
        // PIESELE zidului din jurul golurilor lui cotate (`fem.pereti_cu_goluri`):
        // șpaleții pe toată înălțimea, parapetul de sub fereastră, buiandrugul
        // de peste gol — așa se vede zidul de lângă ușă și de lângă geam, nu o
        // cutie plină cu o casetă translucidă în ea. Fără piese (perete fără
        // goluri cotate, sau rezultat vechi) rămâne cutia întreagă. Toate
        // piesele poartă același `ref`: la click e tot peretele.
        const ref = iZid++;
        const piese = w.piese ?? [];
        const bucati: { inele: [number, number][][]; z0: number; z1: number }[] =
          piese.length
            ? piese.map((p) => ({ inele: p.inele, z0: p.z0_m, z1: p.z1_m }))
            : [{ inele, z0: 0, z1: hEtaj }];
        bucati.forEach((b) => {
          if (!b.inele.length || b.inele[0].length < 3 || b.z1 - b.z0 <= 0) return;
          const geo = new THREE.ExtrudeGeometry(forma(b.inele), {
            depth: b.z1 - b.z0, bevelEnabled: false,
          });
          const mesh = new THREE.Mesh(geo, matPereti);
          // de la pardoseala nivelului (sub placa lui), cu cota piesei
          mesh.position.z = et.z_m - hEtaj + b.z0;
          mesh.userData = { tip: "perete_zidarie", ref, unghi: unghiZid };
          gPereti.add(mesh);
          pickables.push(mesh);
        });
      });
    });
    asaza(gPereti, ORD.invelis);
    scene.add(gPereti);

    // --- GOLURILE: ferestrele și ușile importate — clicabile -----------------
    // Sunt ARHITECTURĂ, nu structură: nu intră în niciun calcul, se desenează
    // ca să se poată verifica ce a citit importul de pe planuri. De aceea
    // stau pe layere proprii, stinse implicit.
    //
    // Cotele: fața de jos la `hp_m` peste PARDOSEALA nivelului (marcajul
    // „HP=" al planșei), iar înălțimea `h_m` măsurată pe planșa de fațade.
    // Pardoseala nivelului e `z_m − h_etaj`, adică fața de sus a plăcii de
    // dedesubt — același reper de la care cotează arhitectul.
    //
    // Un gol căruia dosarul nu-i dă `hp_m` sau `h_m` NU se desenează ca o
    // cutie cu cote presupuse: rămâne o fâșie subțire pe pardoseală, la
    // poziția și lățimea lui, care sunt măsurate. Așa se vede din scenă că
    // golul există și că nu i se cunoaște înălțimea, în loc să arate identic
    // cu unul măsurat («stăm true la arhitectura
    // importată»).
    const GOL_NECOTAT_M = 0.05;      // grosimea fâșiei „știu unde, nu știu cât"
    const gFerestre = new THREE.Group();
    const gUsi = new THREE.Group();
    // golul stă ÎN grosimea zidului, deci fețele lui sunt coplanare cu ale
    // lui: `ORD.gol` > `ORD.invelis` îl pune mereu peste zid, în loc să se
    // schimbe între ele la fiecare rotire
    const matFereastra = invelis(new THREE.MeshLambertMaterial({
      color: PAL_LAYERE_3D.ferestre,
      transparent: true, opacity: 0.5, side: THREE.FrontSide,
    }));
    const matUsa = invelis(new THREE.MeshLambertMaterial({
      color: PAL_LAYERE_3D.usi,
      transparent: true, opacity: 0.5, side: THREE.FrontSide,
    }));
    let iFer = 0;
    let iUsa = 0;
    model.etaje.forEach((et) => {
      const hEtaj = et.inaltime_m ?? r.model.inaltime_etaj_m ?? 3;
      const zPardoseala = et.z_m - hEtaj;
      (et.goluri ?? []).forEach((g) => {
        const usa = g.tip === "usa";
        // indicii cresc pe FIECARE listă separat, în ordinea desenării —
        // exact ordinea din goluriFlat()
        const ref = usa ? iUsa++ : iFer++;
        if ((g.colturi ?? []).length < 3) return;
        const shape = new THREE.Shape(
          g.colturi.map(([x, y]) => new THREE.Vector2(x, y)));
        const cotat = g.hp_m != null && g.h_m != null;
        const depth = cotat ? g.h_m! : GOL_NECOTAT_M;
        const geo = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false });
        const mesh = new THREE.Mesh(geo, usa ? matUsa : matFereastra);
        mesh.position.z = zPardoseala + (cotat ? g.hp_m! : 0);
        mesh.userData = { tip: usa ? "usa" : "fereastra", ref };
        (usa ? gUsi : gFerestre).add(mesh);
        pickables.push(mesh);
      });
    });
    asaza(gFerestre, ORD.gol);
    asaza(gUsi, ORD.gol);
    scene.add(gFerestre);
    scene.add(gUsi);

    // --- PARAPETELE: marginile nestructurale ale Pasului 2 — clicabile -------
    // Planul le dă conturul și grosimea, măsurate; înălțimea vine din model
    // (`h_m`): cotată în dosar, sau presupusă 0,90 m când planul n-o dă — cu
    // ea parapetul intră în încărcări și mase, deci așa se și desenează.
    // Un rezultat vechi, fără `h_m`, rămâne bandă joasă pe pardoseală.
    const gParapete = new THREE.Group();
    const matParapet = invelis(new THREE.MeshLambertMaterial({
      color: PAL_LAYERE_3D.margine,
      transparent: true, opacity: 0.75, side: THREE.FrontSide,
    }));
    let iPar = 0;
    model.etaje.forEach((et) => {
      const hEtaj = et.inaltime_m ?? r.model.inaltime_etaj_m ?? 3;
      (et.margini ?? []).forEach((m) => {
        const inele = m.inele ?? [];
        if (inele.length === 0 || inele[0].length < 3) { iPar++; return; }
        const shape = new THREE.Shape(
          inele[0].map(([x, y]) => new THREE.Vector2(x, y)));
        inele.slice(1).forEach((hole) => {
          shape.holes.push(new THREE.Path(hole.map(([x, y]) => new THREE.Vector2(x, y))));
        });
        const geo = new THREE.ExtrudeGeometry(
          shape, { depth: m.h_m ?? GOL_NECOTAT_M, bevelEnabled: false });
        const mesh = new THREE.Mesh(geo, matParapet);
        mesh.position.z = et.z_m - hEtaj;
        mesh.userData = { tip: "parapet", ref: iPar++ };
        gParapete.add(mesh);
        pickables.push(mesh);
      });
    });
    asaza(gParapete, ORD.parapet);
    scene.add(gParapete);

    // --- NUCLEUL: casele de scară și lifturile (zonele `scara`/`lift` ale
    //     Pasului 2) — doar afișare. Structuristul pune pereții mari
    //     perpendiculari pe clădire și câteva elemente în jurul scărilor și
    //     al lifturilor, deci locul lor trebuie să se vadă: o prismă
    //     translucidă pe toată înălțimea etajului, verde la scară, ocru la
    //     lift, iar în casa scării TREPTELE ei — cele citite de pe planșă
    //     (`nc.scara`, rampele cu contratreptele lor) sau, fără ele, scara
    //     tipului în cutia casei (`scaraImplicita`, regula din
    //     `viewer/plan/scara.ts`). Nu e clicabil: nu e element al modelului.
    const gNuclee = new THREE.Group();
    const matScara = invelis(new THREE.MeshLambertMaterial({
      color: CULORI.scara, transparent: true, opacity: 0.35,
      side: THREE.FrontSide,
    }));
    const matLift = invelis(new THREE.MeshLambertMaterial({
      color: CULORI.lift, transparent: true, opacity: 0.35,
      side: THREE.FrontSide,
    }));
    const matTreapta = new THREE.MeshLambertMaterial({ color: CULORI.scara });
    model.etaje.forEach((et) => {
      const hEtaj = et.inaltime_m ?? r.model.inaltime_etaj_m ?? 3;
      (et.nuclee ?? []).forEach((nc) => {
        const inele = nc.inele ?? [];
        if (inele.length === 0 || inele[0].length < 3) return;
        const shape = new THREE.Shape(
          inele[0].map(([x, y]) => new THREE.Vector2(x, y)));
        inele.slice(1).forEach((hole) => {
          shape.holes.push(new THREE.Path(hole.map(([x, y]) => new THREE.Vector2(x, y))));
        });
        const geo = new THREE.ExtrudeGeometry(
          shape, { depth: hEtaj, bevelEnabled: false });
        const mesh = new THREE.Mesh(geo, nc.kind === "lift" ? matLift : matScara);
        mesh.position.z = et.z_m - hEtaj;
        mesh.userData = { tip: "nucleu", kind: nc.kind, id: nc.id };
        gNuclee.add(mesh);
        if (nc.kind !== "scara") return;
        const scara = nc.scara?.rampe?.length ? nc.scara : scaraImplicita(inele[0], hEtaj);
        if (!scara) return;
        const { trepte, podeste } = treptele(scara, et.z_m - hEtaj, hEtaj);
        trepte.forEach((tr) => {
          const m = new THREE.Mesh(new THREE.BoxGeometry(tr.lung, tr.lat, tr.h), matTreapta);
          m.position.set(tr.cx, tr.cy, tr.z0 + tr.h / 2);
          m.rotation.z = tr.unghi;
          m.userData = { tip: "nucleu", kind: "scara", id: nc.id };
          gNuclee.add(m);
        });
        podeste.forEach((pd) => {
          const m = new THREE.Mesh(new THREE.BoxGeometry(pd.lung, pd.lat, pd.grosime), matTreapta);
          m.position.set(pd.cx, pd.cy, pd.z - pd.grosime / 2);
          m.rotation.z = pd.unghi;
          m.userData = { tip: "nucleu", kind: "scara", id: nc.id };
          gNuclee.add(m);
        });
      });
    });
    asaza(gNuclee, ORD.invelis);
    scene.add(gNuclee);

    // --- pereții de BETON ai subsolului (cutia rigidă) — clicabili -----------
    const gPeretiBeton = new THREE.Group();
    // panoul de beton e ȚESUT cu stâlpul de la capăt: fețele lor sunt în
    // același plan. Cedează stâlpului — opac, deci scrie adâncime ca înainte.
    const matPereteBeton = realist
      ? invelis(materialRealist("pereteBeton"), CEDEAZA.pereteBeton)
      : cedeaza(new THREE.MeshLambertMaterial({ color: CULORI.pereteBeton }),
                CEDEAZA.pereteBeton);
    (model.pereti_beton ?? []).forEach((p, i) => {
      const [ax0, ay0] = p.a_xy;
      const [bx0, by0] = p.b_xy;
      const dx = bx0 - ax0;
      const dy = by0 - ay0;
      const L = Math.hypot(dx, dy);
      if (L < 1e-6) return;
      // CE ÎNĂLȚIME SE DESENEAZĂ: cea TURNATĂ (`h_desen_m`), între fața de
      // jos scrisă de model (`z_jos_m` — fundația de sub panou sau placa pe
      // care stă) și fața de jos a plăcii/grinzii de deasupra. `h_web_m` e
      // înălțimea de CALCUL, măsurată de la fața de jos a plăcii în jos: cu
      // ea, panoul se oprea la +7,5 cm, iar betonul fundației începe la
      // −h_peste (−30 cm pe „Bloc Olteniței P + 4") — 37,5 cm de aer sub un
      // perete de beton armat («trebuie să se sprijine
      // direct pe fundație sau pe placă; nu trebuie să fie gol sub ei»).
      // Panourile de BALCON n-au cotă de jos scrisă (nu stau pe
      // infrastructură): pentru ele rămâne coborârea din offsetul compozit.
      const hPb = p.h_desen_m ?? p.h_web_m;
      const zJosPb = p.z_jos_m ?? (
        p.z_sus_m - (p.e_offset_m ?? p.hf_m / 2 + p.h_web_m / 2)
        - p.h_web_m / 2);
      const geo = new THREE.BoxGeometry(L, p.t_m, hPb);
      const mesh = new THREE.Mesh(geo, matPereteBeton);
      mesh.position.set(
        (ax0 + bx0) / 2,
        (ay0 + by0) / 2,
        zJosPb + hPb / 2
      );
      mesh.rotation.z = Math.atan2(dy, dx);
      // `unghi` e direcția peretelui în plan, pentru cotele lui (v.
      // `coteObiect3d`): fără el, un perete pe o clădire rotită și-ar scrie
      // grosimea și lungimea pe axele lumii, deci pe niciuna dintre fețele
      // lui.
      mesh.userData = {
        tip: "perete_beton", ref: i, unghi: Math.atan2(dy, dx),
      };
      gPeretiBeton.add(mesh);
      pickables.push(mesh);
    });
    asaza(gPeretiBeton, ORD.pereteBeton);
    scene.add(gPeretiBeton);

    // --- ZIDĂRIA CONFINATĂ a etajului retras-proeminență: pereții
    //     structurali de zidărie (panourile dintre stâlpi, ca la subsol) și
    //     stâlpișorii lor de beton — clicabili ---------------------------------
    const gZidarieConfinata = new THREE.Group();
    const matZc = realist
      ? invelis(materialRealist("zid"), CEDEAZA.zidarieConfinata)
      : cedeaza(new THREE.MeshLambertMaterial({ color: CULORI.zidarieConfinata }),
                CEDEAZA.zidarieConfinata);
    // stâlpișorul are culoarea LUI, din familia zidăriei (v. `paletaDesen`):
    // în albastrul stâlpului s-ar citi ca un ESV pus de solver
    const matStalpisor = realist
      ? invelis(materialRealist("stalp"), CEDEAZA.zidarieConfinata)
      : new THREE.MeshLambertMaterial({ color: CULORI.stalpisorZc });
    (model.pereti_zidarie ?? []).forEach((p, i) => {
      const [ax0, ay0] = p.a_xy;
      const [bx0, by0] = p.b_xy;
      const dx = bx0 - ax0;
      const dy = by0 - ay0;
      const L = Math.hypot(dx, dy);
      if (L < 1e-6) return;
      // zidăria stă pe placa (sau grinda) de dedesubt și urcă până sub
      // grinda ori placa de deasupra: cota de jos din offsetul panoului
      const hZc = p.h_web_m;
      const zJosZc = p.z_sus_m - (p.e_offset_m ?? p.hf_m / 2 + p.h_web_m / 2)
        - p.h_web_m / 2;
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(L, p.t_m, hZc), matZc);
      mesh.position.set((ax0 + bx0) / 2, (ay0 + by0) / 2, zJosZc + hZc / 2);
      mesh.rotation.z = Math.atan2(dy, dx);
      mesh.userData = {
        tip: "zidarie_confinata", ref: i, unghi: Math.atan2(dy, dx),
      };
      gZidarieConfinata.add(mesh);
      pickables.push(mesh);
    });
    (model.stalpisori_zc ?? []).forEach((st) => {
      const h = st.z_sus_m - st.z_jos_m;
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(st.latura_m, st.t_m, h), matStalpisor);
      mesh.position.set(st.xy[0], st.xy[1], st.z_jos_m + h / 2);
      mesh.rotation.z = Math.atan2(st.u[1], st.u[0]);
      gZidarieConfinata.add(mesh);
    });
    asaza(gZidarieConfinata, ORD.zidarieConfinata);
    scene.add(gZidarieConfinata);

    // --- balcoanele: consolă de beton (<2 m și, ca terasele, ≥2 m) sau LOGIE
    //     (nu iese din amprentă — nu e consolă) ----------------------------
    // Consola de peste 2 m (de obicei terasa) are aceeași culoare ca balconul:
    // un ocru de semnal ar sări în ochi mai tare decât stâlpii și pereții,
    // iar ea e tot o placă în consolă
    const gBalcoane = new THREE.Group();
    const matBalconBeton = realist
      ? invelis(materialRealist("balcon"))
      : invelis(new THREE.MeshLambertMaterial({
        color: CULORI.balconBeton,
        transparent: true,
        opacity: 0.9,
      }));
    const matLogie = invelis(new THREE.MeshLambertMaterial({
      color: CULORI.logie,
      transparent: true,
      opacity: 0.35,
    }));
    // TERASA PE PLACĂ (etajul retras peste nivelul de dedesubt) e o parte
    // din ochiul de placă de acolo: desenată ca a doua placă peste el,
    // ochiul se citea din două bucăți, iar selecția lui se vedea pe
    // jumătate. Rămâne doar conturul terasei, întrerupt, pe fața ochiului
    const matTerasa = new THREE.LineDashedMaterial({
      color: CULORI.placa, dashSize: 0.25, gapSize: 0.15,
    });
    model.etaje.forEach((et) => {
      (et.balcoane ?? []).forEach((b) => {
        if (!b.contur || b.contur.length < 3) return;
        if (b.tip === "terasa_pe_placa") {
          const linie = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints(
              [...b.contur, b.contur[0]].map(
                ([x, y]) => new THREE.Vector3(x, y, et.z_m + tPl / 2 + 0.01))),
            matTerasa);
          linie.computeLineDistances();
          gBalcoane.add(linie);
          return;
        }
        const shape = new THREE.Shape(b.contur.map(([x, y]) => new THREE.Vector2(x, y)));
        const geo = new THREE.ExtrudeGeometry(shape, { depth: tPl, bevelEnabled: false });
        const mat = b.tip === "logie" ? matLogie : matBalconBeton;
        const mesh = new THREE.Mesh(geo, mat);
        // placa balconului: aceeași grosime și aceeași față de sus ca placa
        // nivelului din care iese — nodul e în planul median, deci [z−t/2, z+t/2]
        mesh.position.z = et.z_m - tPl / 2;
        gBalcoane.add(mesh);
      });
    });
    asaza(gBalcoane, ORD.balcon);
    scene.add(gBalcoane);

    // --- PLACA PE SOL: reală, dar nestructurală ------------------------------
    // Se toarnă (e în deviz), dar NU e în modelul de calcul: se reazemă direct
    // pe teren. De asta e desenată în GRI translucid, ca zidăria — aceeași
    // convenție pentru „există, dar nu ține structura".
    // Fața de sus la cota 0 (pardoseala nivelului de jos); între ea și fața de
    // sus a fundației rămân balastul și umplutura, care nu se desenează.
    const gPlacaSol = new THREE.Group();
    const psol = placaPeSolDesenata(model);
    if (psol?.contur?.length) {
      const matPlacaSol = invelis(new THREE.MeshLambertMaterial({
        color: CULORI.zidarie,
        transparent: true,
        opacity: 0.55,
        side: THREE.FrontSide,
      }));
      // BALASTUL de sub placă: fără el placa pare că plutește peste gol,
      // fiindcă între fața ei de jos și fața de sus a fundației nu se
      // desenează nimic. E stratul pe care chiar se reazemă.
      const matBalast = invelis(new THREE.MeshLambertMaterial({
        color: PAL_TEREN.necoeziv.culoare,
        transparent: true,
        opacity: 0.5,
        side: THREE.FrontSide,
      }));
      psol.contur.forEach((inele, i) => {
        if (!inele.length || inele[0].length < 3) return;
        const shape = new THREE.Shape(
          inele[0].map(([x, y]) => new THREE.Vector2(x, y)));
        inele.slice(1).forEach((gaura) => {
          shape.holes.push(
            new THREE.Path(gaura.map(([x, y]) => new THREE.Vector2(x, y))));
        });
        const zJos = (psol.z_sus_m ?? 0) - psol.grosime_m;
        const mesh = new THREE.Mesh(
          new THREE.ExtrudeGeometry(shape, {
            depth: psol.grosime_m,
            bevelEnabled: false,
          }),
          matPlacaSol);
        mesh.position.z = zJos;
        mesh.userData = { tip: "placa_pe_sol", ref: i };
        pickables.push(mesh);
        gPlacaSol.add(mesh);
        if (psol.balast_m) {
          const balast = new THREE.Mesh(
            new THREE.ExtrudeGeometry(shape, {
              depth: psol.balast_m,
              bevelEnabled: false,
            }),
            matBalast);
          balast.position.z = zJos - psol.balast_m;
          gPlacaSol.add(balast);
        }
      });
    }
    asaza(gPlacaSol, ORD.invelis);
    scene.add(gPlacaSol);

    // --- FUNDAȚIA: betonul turnat + rețeaua de calcul -----------------------
    // Fundația E în model (fem_fundatie.py): sub fiecare ax al Pasului 5, o
    // bară `elasticBeamColumn` NEfisurată între nodurile de bază, iar în
    // fiecare nod un `zeroLength` cu k_v = K_s·A_aferentă pe direcția 3 și
    // k_h = r_h·k_v pe 1 și 2, legat de un nod de sol încastrat.
    //
    // În 3D se desenează NUMAI CE E FIZIC („ce reprezintă
    // acele arcuri de sub fundație? dacă nu sunt ceva fizic, trebuie scos"):
    //   * BETONUL — secțiunea de execuție a Pasului 6, între fața de sus
    //     (−h_peste) și cota de fundare (−D_f). La detaliul «T»: talpa B×h_t
    //     jos, elevația b_i×h_i peste ea, fiecare ax cu lățimea LUI;
    //   * porțiunea de STÂLP dintre cota 0 și fața de sus a fundației: stâlpul
    //     coboară în elevație, monolit cu ea. Fără ea, betonul desenat la
    //     cotele lui reale lăsa stâlpii să plutească la 30 cm deasupra tălpii
    //     („văd o distanță între stâlpi și fundație", aceeași zi) — o distanță
    //     care nici în model nu există: acolo nodul de bază E pe grinda de
    //     fundare.
    // NU se desenează: resoartele Winkler (simboluri de calcul, nu beton — k_v,
    // k_h, pasul și K_s sunt cifre în panoul axului), umplutura de pământ (se
    // va reprezenta separat) și AXELE BARELOR de fundare. Axele au fost un
    // layer o zi: la cota 0 ele trec prin beton și prin porțiunea de stâlp,
    // deci nu se pot vedea oricum („nu își au rostul în 3D, că nu ai cum să le
    // vezi" — Stefan). Idealizarea se explică în panoul axului,
    // unde e text.
    const gFundatie = new THREE.Group();
    const { fnd, grinzi: grinziFund } = fundareaDesenata(model);
    const radiere = radiereDesenate(model);
    const detaliuFund = fnd.detaliu ?? "T";
    const hPesteFund = fnd.h_peste_m ?? 0.3;
    const hCFund = fnd.H_c_m ?? 0.6;
    // talpa și inima se ating pe toată fața lor comună, iar prin elevație
    // coboară stâlpul: la egalitate cedează amândouă
    const matFundatie = realist
      ? invelis(materialRealist("fundatie"), CEDEAZA.fundatie)
      : cedeaza(new THREE.MeshLambertMaterial({ color: CULORI.fundatie }),
                CEDEAZA.fundatie);
    const matFundatieSimplu = invelis(new THREE.MeshLambertMaterial({
      color: CULORI.beton,
      transparent: true,
      opacity: 0.9,
    }));
    // fața de sus și fața de jos ale betonului, măsurate din cota 0 (nodul de
    // bază al stâlpilor): D_f e adâncimea de fundare SUB PARDOSEALĂ
    const zSusFund = -hPesteFund;
    const zJosFundBaza = -(fnd.Df_m || hPesteFund + hCFund);
    // …dar cota de jos e a AXULUI: fiecare ax se toarnă la
    // adâncimea cerută de deschiderea LUI, iar desenul trebuie să arate
    // treptele de racordare, nu o talpă plană inexistentă
    const zJosAx = (g: { ax?: string; Df_m?: number }) => {
      const d = g.Df_m ?? (g.ax ? fnd.pe_ax?.[g.ax]?.Df_m : undefined);
      return d ? -d : zJosFundBaza;
    };
    // planul grilei = cota de fundare cea mai de JOS (fața de jos a betonului)
    // …iar pe radier, fața de jos a balastului lui
    const zTeren = grinziFund.length || radiere.length
      ? Math.min(grinziFund.length ? zJosFundBaza : 0,
                 ...grinziFund.map(zJosAx),
                 ...radiere.map((x) => x.z_jos_m - (x.balast_m ?? 0)))
      : -0.02;

    grinziFund.forEach((g, i) => {
      const a = nd.get(g.nod_i);
      const b = nd.get(g.nod_j);
      if (!a || !b) return;
      const dx = b.x_m - a.x_m;
      const dy = b.y_m - a.y_m;
      const L = Math.hypot(dx, dy);
      if (L < 1e-6) return;
      const pe = fnd.pe_ax?.[g.ax];
      const bAx = g.B_m || pe?.B_m || fnd.B_m || 0.6;
      const zJosFund = zJosAx(g);
      const hCAx = g.H_c_m ?? pe?.H_c_m ?? hCFund;
      const rot = Math.atan2(dy, dx);
      const cutie = (lat: number, inalt: number, zc: number,
                     mat: THREE.Material, clicabil: boolean) => {
        if (inalt <= 1e-6 || lat <= 1e-6) return;
        const mesh = new THREE.Mesh(new THREE.BoxGeometry(L, lat, inalt), mat);
        mesh.position.set((a.x_m + b.x_m) / 2, (a.y_m + b.y_m) / 2, zc);
        mesh.rotation.z = rot;
        if (clicabil) {
          mesh.userData = { tip: "fundatie", ref: i, ax: g.ax, unghi: rot };
          pickables.push(mesh);
        }
        gFundatie.add(mesh);
      };
      if (detaliuFund === "T") {
        // grosimea tălpii e a AXULUI: un ax lățit cere consolă mai groasă,
        // deci e și mai rigid (sectiune_fundatie.h_talpa_necesara)
        const hT = pe?.h_talpa_m ?? fnd.h_talpa_m ?? 0.3;
        const bInima = pe?.b_inima_m ?? fnd.b_inima_m ?? 0.3;
        const hInima = Math.max(zSusFund - zJosFund - hT, 0);
        cutie(bAx, hT, zJosFund + hT / 2, matFundatie, true);
        cutie(bInima, hInima, zJosFund + hT + hInima / 2, matFundatie, true);
      } else {
        // «bloc» / «pământ»: grinda dreptunghiulară B×H_c sub fața de sus, iar
        // la «bloc» un bloc de beton SIMPLU până la cota de fundare
        cutie(bAx, hCAx, zSusFund - hCAx / 2, matFundatie, true);
        const hBloc = detaliuFund === "bloc" ? (fnd.h_bloc_m ?? 0) : 0;
        cutie(bAx, hBloc, zSusFund - hCAx - hBloc / 2, matFundatieSimplu, false);
      }
    });

    // stâlpul coboară în elevație: porțiunea dintre cota 0 (nodul de bază) și
    // fața de sus a fundației. Fizic e monolită cu elevația — de acolo pleacă
    // mustățile stâlpului — dar în model NU e un element propriu: nodul de bază
    // stă direct pe grinda de fundare. De asta se desenează cu betonul
    // fundației, nu cu structura: dispare împreună cu layerul ei.
    if (grinziFund.length && zSusFund < -1e-6) {
      const noduriFund = new Set<number>();
      grinziFund.forEach((g) => {
        noduriFund.add(g.nod_i);
        noduriFund.add(g.nod_j);
      });
      model.stalpi.forEach((st, i) => {
        const a = nd.get(st.nod_jos);
        if (!a || Math.abs(a.z_m) > 1e-6 || !noduriFund.has(a.id)) return;
        // ACEEAȘI amprentă ca stâlpul de deasupra (lamela alunecată în zid,
        // stâlpul în L), extrudată de la fața de sus a fundației la cota 0.
        // O cutie h × b centrată pe NOD stă, la o lamelă cu nodul la un
        // capăt, metri dincolo de betonul ei — și, clicabilă ca «stâlp»,
        // arată ca un stâlp fără înălțime în altă parte decât stâlpul lui.
        const amp = amprentaFaraPereti(st, amprentaStalp(st, a),
          model.pereti_beton ?? []) ?? amprentaStalp(st, a);
        const mesh = new THREE.Mesh(new THREE.ExtrudeGeometry(
          new THREE.Shape(amp.map(([x, y]) =>
            new THREE.Vector2(x - a.x_m, y - a.y_m))),
          { depth: -zSusFund, bevelEnabled: false }), matStalp);
        mesh.position.set(a.x_m, a.y_m, zSusFund);
        // clickul pe ea deschide STÂLPUL: e stâlpul, chiar dacă porțiunea
        // asta n-are element de calcul
        mesh.userData = {
          tip: "stalp", ref: i,
          unghi: st.dir ? Math.atan2(st.dir[1], st.dir[0]) : 0,
        };
        gFundatie.add(mesh);
        pickables.push(mesh);
      });
    }

    // --- RADIERUL: placa groasă pe care stă tot --------------------------------
    // Pe radier nu există grinzi de fundare: fundația e o placă de h_r pe
    // toată amprenta, de la pardoseala nivelului de jos în jos, pe balast.
    // E betonul fundației, deci stă în layerul ei și poartă culoarea ei; pe
    // complex fiecare corp îl are pe al lui, la cota lui. Până să fie
    // desenat, sub un turn pe radier groapa cobora la −0,90 m iar placa
    // desenată era cea de 15 cm: un gol de 65 cm sub clădire, vizibil cu
    // layerul «teren și săpătură» aprins.
    const matBalastRadier = invelis(new THREE.MeshLambertMaterial({
      color: PAL_TEREN.necoeziv.culoare,
      transparent: true,
      opacity: 0.5,
      side: THREE.FrontSide,
    }));
    radiere.forEach((rad, i) => {
      rad.contur.forEach((inele) => {
        if (!inele.length || inele[0].length < 3) return;
        const shape = new THREE.Shape(
          inele[0].map(([x, y]) => new THREE.Vector2(x, y)));
        inele.slice(1).forEach((gaura) => {
          shape.holes.push(
            new THREE.Path(gaura.map(([x, y]) => new THREE.Vector2(x, y))));
        });
        const mesh = new THREE.Mesh(
          new THREE.ExtrudeGeometry(shape, { depth: rad.h_m, bevelEnabled: false }),
          matFundatie);
        mesh.position.z = rad.z_jos_m;
        mesh.userData = { tip: "radier", ref: i };
        pickables.push(mesh);
        gFundatie.add(mesh);
        if (rad.balast_m) {
          const balast = new THREE.Mesh(
            new THREE.ExtrudeGeometry(shape, { depth: rad.balast_m, bevelEnabled: false }),
            matBalastRadier);
          balast.position.z = rad.z_jos_m - rad.balast_m;
          gFundatie.add(balast);
        }
      });
    });

    asaza(gStalpi, ORD.stalp);
    asaza(gGrinzi, ORD.grinda);
    asaza(gFundatie, ORD.fundatie);
    scene.add(gFundatie);

    // --- ce ADAUGĂ layerul «Realist» ----------------------------------------
    // Muchiile intră ÎN grupul familiei, nu lângă el: așa se sting împreună
    // cu ea, fără nicio ramură în panoul de vizibilitate.
    let mediu: { env: THREE.Texture; dispose: () => void } | null = null;
    let lumini: { ceruUmbra: () => void; dispose: () => void } | null = null;
    const deAruncat: { dispose: () => void }[] = [];
    if (realist) {
      mediu = mediuRealist(renderer);
      scene.environment = mediu.env;
      // mediul dă REFLEXUL, nu lumina difuză: la intensitate plină,
      // cutiile albe ale camerei de studio spală betonul până la alb și
      // volumele dispar. Aici e ținut jos, iar modelarea vine din cheie.
      scene.environmentIntensity = 0.38;
      // cota terenului = baza STRUCTURII (nodurile de bază ale stâlpilor),
      // nu fundul cutiei: sub ea e infrastructura, iar umbra trebuie să cadă
      // pe teren, în jurul clădirii
      const zTeren = model.stalpi.reduce(
        (z, st) => Math.min(z, nd.get(st.nod_jos)?.z_m ?? z), iz.max);
      lumini = luminiRealist(scene, renderer,
        new THREE.Vector3(cx, cy, cz), span,
        Number.isFinite(zTeren) ? zTeren : iz.min);
      [gStalpi, gGrinzi, gPlaca, gBalcoane, gPeretiBeton].forEach(aruncaUmbra);
      ([[gStalpi, "stalp", ORD.stalp], [gGrinzi, "grinda", ORD.grinda],
        [gPlaca, "placa", ORD.placa], [gBalcoane, "balcon", ORD.balcon],
        [gPeretiBeton, "pereteBeton", ORD.pereteBeton],
        [gPereti, "zid", ORD.invelis],
        [gFundatie, "fundatie", ORD.fundatie]] as const).forEach(
        ([grup, fam, ord]) => {
          const m = muchiiFamilie(grup, fam);
          if (!m) return;
          m.renderOrder = ord * 100000 + 99999;
          grup.add(m);
          deAruncat.push(m.geometry, m.material as THREE.Material);
        });
      // umbra clădirii se calculează o dată: scena e statică, doar camera se
      // mișcă (v. `luminiRealist`)
      lumini.ceruUmbra();
    }
    // armătura: grup propriu, umplut abia când vine rezumatul de la server
    const gArmatura = new THREE.Group();
    scene.add(gArmatura);
    let armaturaCladita: GrupArmatura | null = null;
    // rezumatul curent: din el se citesc CLASELE pieselor de beton la
    // evidențiere (id-ul de plan → clasa)
    let rezArmatura: ArmaturaRezumat | null = null;

    // --- TERENUL și SĂPĂTURA -------------------------------------------------
    // Nu sunt în modelul de calcul: pământul din jurul clădirii și volumul
    // din care el a fost scos. Geometria vine de-a gata din `model.sapatura`
    // (server: `sapatura.py`) — aceeași din care ies volumele devizului.
    // Prisma de teren se întinde peste clădire cu o margine, ca malurile
    // săpăturii să aibă în ce sta.
    // …și se construiește LENEȘ, la prima bifă: norul de puncte costă ~60 ms,
    // iar layerul e stins implicit — nicio pagină care nu-l cere nu-l plătește.
    const gTerenSapatura = new THREE.Group();
    gTerenSapatura.visible = false;
    let terenConstruit = false;
    const construiesteTeren = () => {
      if (terenConstruit || !model.sapatura) return;
      terenConstruit = true;
      const marja = Math.max(2.0, span * 0.08);
      const xsSap = [ix.min, ix.max];
      const ysSap = [iy.min, iy.max];
      (model.sapatura.groapa?.inel_sus ?? []).forEach((inel) =>
        inel.forEach(([x, y]) => {
          xsSap[0] = Math.min(xsSap[0], x);
          xsSap[1] = Math.max(xsSap[1], x);
          ysSap[0] = Math.min(ysSap[0], y);
          ysSap[1] = Math.max(ysSap[1], y);
        }));
      gTerenSapatura.add(grupTerenSapatura(model.sapatura, {
        xmin: xsSap[0] - marja, xmax: xsSap[1] + marja,
        ymin: ysSap[0] - marja, ymax: ysSap[1] + marja,
      }));
    };
    scene.add(gTerenSapatura);

    // --- COTELE: câteva cote importante, tot leneș ---------------------------
    const gCote = new THREE.Group();
    gCote.visible = false;
    let coteConstruite = false;
    const construiesteCote = () => {
      if (coteConstruite) return;
      coteConstruite = true;
      gCote.add(grupCote(r, {
        xmin: ix.min, xmax: ix.max,
        ymin: iy.min, ymax: iy.max,
        zmin: iz.min, zmax: iz.max,
      }));
    };
    scene.add(gCote);

    // Încadrarea: prima dată când se aprinde un layer care iese mult în afara
    // clădirii (terenul coboară 25 m, cotele ies în lateral), camera rămâne
    // strânsă pe noduri și omul vede o felie. Se reîncadrează O SINGURĂ DATĂ,
    // pe ce e vizibil atunci — după aia, cadrul e al omului. Clădirea cu
    // terenul ei umple ecranul și îl depășesc puțin (3 %): cutia trebuie să
    // atingă marginile, nu să plutească în mijloc.
    //
    // …dar NU să le depășească: la +3 % etichetele cotelor de la
    // margine — lungimile, «−0,50 FUNDARE» — ieșeau din cadru chiar când le
    // aprindeai ca să le citești. Cu o margine de 10 % tot ce aprinzi încape.
    let incadrat = false;
    const incadreaza = (obiect: THREE.Object3D) => {
      const cutie = cutiaFizica(obiect);
      cutie.expandByPoint(new THREE.Vector3(ix.min, iy.min, iz.min));
      cutie.expandByPoint(new THREE.Vector3(ix.max, iy.max, iz.max));
      if (cutie.isEmpty()) return;
      incadreazaCutia(camera, controls.target, cutie, -0.1);
      controls.update();
    };

    // --- nodurile FEM (punctele de împărțire) --------------------------------
    const gNoduri = new THREE.Group();
    const addInst = (ids: number[], radius: number, color: number) => {
      if (!ids.length) return;
      const inst = new THREE.InstancedMesh(
        new THREE.SphereGeometry(radius, 10, 10),
        new THREE.MeshLambertMaterial({ color }),
        ids.length
      );
      const m4 = new THREE.Matrix4();
      ids.forEach((id, k) => {
        const n = nd.get(id)!;
        m4.setPosition(n.x_m, n.y_m, n.z_m);
        inst.setMatrixAt(k, m4);
      });
      gNoduri.add(inst);
    };
    const byTip = (tip: string) =>
      model.noduri.filter((n) => n.tip === tip).map((n) => n.id);
    addInst(byTip("fem"), 0.055, CULORI.nodFem);
    addInst(byTip("reazem"), 0.09, CULORI.nodReazem);
    addInst(byTip("stalp"), 0.07, CULORI.nodFem);
    addInst(byTip("baza"), 0.1, CULORI.ink);
    scene.add(gNoduri);

    // --- mesh-ul plăcii (varianta shell) ---------------------------------------
    const gMesh = new THREE.Group();
    if (model.shells?.length) {
      const edges = new Set<string>();
      model.shells.forEach((sh) => {
        const q = sh.noduri;
        for (let k = 0; k < 4; k++) {
          const a = q[k];
          const b = q[(k + 1) % 4];
          edges.add(a < b ? `${a},${b}` : `${b},${a}`);
        }
      });
      const pos = new Float32Array(edges.size * 6);
      let k = 0;
      edges.forEach((e) => {
        const [a, b] = e.split(",").map(Number);
        const na = nd.get(a)!;
        const nb = nd.get(b)!;
        pos.set([na.x_m, na.y_m, na.z_m + 0.01, nb.x_m, nb.y_m, nb.z_m + 0.01], k);
        k += 6;
      });
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
      gMesh.add(new THREE.LineSegments(
        geo,
        new THREE.LineBasicMaterial({
          color: CULORI.placa, transparent: true, opacity: 0.35,
          depthWrite: false,
        })
      ));

    }
    // rețeaua de elemente finite e desenul plăcii — se vede din ce e făcut
    // fiecare ochi; pornită implicit, dar cu comutatorul ei, ca orice layer
    asaza(gMesh, ORD.mesh);
    scene.add(gMesh);

    // --- CM / master per diafragmă (doar varianta lean) -----------------------
    const gCm = new THREE.Group();
    const matCm = new THREE.MeshLambertMaterial({ color: CULORI.masa });
    (model.diafragme ?? []).forEach((d) => {
      const s = new THREE.Mesh(new THREE.SphereGeometry(0.16, 16, 16), matCm);
      s.position.set(d.cm[0], d.cm[1], d.z_m + 0.02);
      gCm.add(s);
      // cruce prin CM, ca să fie vizibil pe placă
      const mk = (dx: number, dy: number) => {
        const pts = [
          new THREE.Vector3(d.cm[0] - dx, d.cm[1] - dy, d.z_m + 0.02),
          new THREE.Vector3(d.cm[0] + dx, d.cm[1] + dy, d.z_m + 0.02),
        ];
        gCm.add(
          new THREE.Line(
            new THREE.BufferGeometry().setFromPoints(pts),
            new THREE.LineBasicMaterial({ color: CULORI.masa })
          )
        );
      };
      mk(0.8, 0);
      mk(0, 0.8);
    });
    scene.add(gCm);

    // GRILA E RARĂ ȘI PALIDĂ. O linie pe metru însemna, pe o clădire de 40 m,
    // ~56 de linii de un pixel pe fiecare direcție; privite razant ele cad sub
    // un pixel, se sting și se aprind de la un cadru la altul și dau exact
    // sclipirea de moar pe care ochiul o citește ca pâlpâire — deși niciun
    // element nu se bate cu altul. La 5 m pasul rămâne citibil ca reper de
    // scară, dar liniile nu se mai calcă între ele.
    const latura = Math.ceil(span * 1.4 / PAS_GRILA_M) * PAS_GRILA_M;
    const grid = new THREE.GridHelper(latura, latura / PAS_GRILA_M,
                                      CULORI.grilaMare, CULORI.grilaMica);
    (Array.isArray(grid.material) ? grid.material : [grid.material])
      .forEach((m) => {
        const lm = m as THREE.LineBasicMaterial;
        lm.transparent = true;
        lm.opacity = 0.55;
        lm.depthWrite = false;
      });
    // grila e REPER DE SCARĂ, nu materie: `cutiaFizica` (deci și încadrarea de
    // la tasta «0») trebuie s-o sară, altfel cadrul s-ar strânge pe ea — e cu
    // 40 % mai lată decât clădirea, iar vederea ar rămâne mereu prea depărtată
    grid.userData.adnotare = true;
    grid.rotation.x = Math.PI / 2;
    // planul grilei = COTA DE FUNDARE, când fundația e desenată (altfel grila
    // ar tăia talpa pe la jumătate)
    grid.position.set(cx, cy, zTeren - 0.02);
    scene.add(grid);

    // --- click -> selecție -----------------------------------------------------
    // three nu ține cont de `visible` la raycast, iar zidăria e implicit
    // ascunsă: fără filtrul ăsta, un click prin clădire ar selecta un perete
    // pe care omul nici nu-l vede. Aceeași întrebare o pune și cotarea, care
    // nu are voie să măsoare până la o piesă stinsă din panou.
    const vizibil = (o: THREE.Object3D | null): boolean =>
      !o || (o.visible !== false && vizibil(o.parent));
    // PLANUL DE TĂIERE, așa cum îl vede clickul. `null` = nimeni nu taie
    // nimic, adică scena de până acum, cu selecția ei neatinsă.
    let planCurent: THREE.Plane | null = null;
    // Ce piese se UMPLU la tăietură, deci opresc raza pe fața tăiată:
    // volumele, nu golurile. Prin fereastra tăiată se vede mai departe, deci
    // clickul trece prin ea — cum trece și privirea. Se strânge la primul
    // click, când grupurile sunt gata.
    let pieseCuCapac: Set<THREE.Object3D> | null = null;
    const areCapac = (o: THREE.Object3D): boolean => {
      if (!pieseCuCapac) {
        pieseCuCapac = new Set<THREE.Object3D>();
        volumeSectiune.forEach((g) => g.traverse((x) => {
          if ((x as THREE.Mesh).isMesh && !x.userData?.stencil)
            pieseCuCapac!.add(x);
        }));
      }
      return pieseCuCapac.has(o);
    };
    const ray = new THREE.Raycaster();
    let down: [number, number] | null = null;
    const onDown = (e: PointerEvent) => {
      down = [e.clientX, e.clientY];
    };
    // piesa de sub cursor, sau `null`: aceeași alegere pentru click (o
    // selectează) și dublu-click (îi deschide pagina)
    const alegeLa = (clientX: number, clientY: number): Selectie => {
      const rect = renderer.domElement.getBoundingClientRect();
      const p = new THREE.Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1
      );
      ray.setFromCamera(p, camera);
      const lovite = ray
        .intersectObjects(pickables, false)
        .filter((h) => vizibil(h.object));
      // CE E SUB CURSOR ÎNTR-O CLĂDIRE TĂIATĂ (v. `laTaietura`): loviturile
      // din jumătatea aruncată nu mai sunt pe ecran, iar pe fața tăiată se
      // vede capacul — adică tocmai piesa prin care raza a trecut. Fără
      // asta, clickul pe secțiune alege fațada pe care secțiunea a
      // aruncat-o, sau piesa din spatele peretelui tăiat.
      const subCursor = planCurent
        ? laTaietura(lovite, ray.ray, planCurent, areCapac)
        : { ramase: lovite, capac: [] as THREE.Object3D[] };
      const candidati: THREE.Object3D[] = subCursor.capac.length
        ? subCursor.capac
        : subCursor.ramase.map((h) => h.object);
      // Cât timp layerul «placă» e BIFAT, ochiul de placă are prioritate la
      // click («acum încerc să dau click și îmi selectează
      // un stâlp din spatele ei»). Prima variantă dădea prioritate barelor,
      // ca să poți selecta o grindă privind de sus — dar atunci placa, care e
      // subiectul paginii, devenea aproape imposibil de nimerit. Grinzile și
      // stâlpii se selectează debifând placa (sau rotind sub planșeu).
      const ales = gPlaca.visible
        ? candidati[0]
        : candidati.find(
            (o) => (o.userData as { tip: string }).tip !== "panou"
          ) ?? candidati[0];
      if (!ales) return null;
      const u = ales.userData as {
        tip: "grinda" | "stalp" | "perete_beton" | "perete_zidarie" | "panou"
           | "fundatie" | "fereastra" | "usa" | "parapet"
           | "zidarie_confinata" | "placa_pe_sol" | "radier";
        ref: number;
      };
      return { tip: u.tip, ref: u.ref };
    };
    const onUp = (e: PointerEvent) => {
      if (!down) return;
      const moved = Math.hypot(e.clientX - down[0], e.clientY - down[1]);
      down = null;
      if (moved > 5) return; // a fost orbit/pan, nu click
      onSel(alegeLa(e.clientX, e.clientY));
    };
    // DUBLU-CLICK pe o piesă = pagina ei: obiectul selectat se deschide
    // direct, fără drum prin panou. Cele două clickuri au selectat-o deja;
    // aici doar se pleacă.
    const onDbl = (e: MouseEvent) => {
      const s = alegeLa(e.clientX, e.clientY);
      if (s) onDeschideRef.current?.(s);
    };
    renderer.domElement.addEventListener("pointerdown", onDown);
    renderer.domElement.addEventListener("pointerup", onUp);
    renderer.domElement.addEventListener("dblclick", onDbl);

    // --- highlight + vizibilitate (expuse către React) -------------------------
    // materialul cu care se REÎNTOARCE o piesă după ce nu mai e selectată.
    // Familiile lipsă cădeau toate pe `matGrinda`, deci o fereastră
    // deselectată rămânea vopsită ca o grindă — de aici lista completă, cu
    // fiecare tip care ajunge în `pickables`.
    const matDupaTip = (tip: string) =>
      tip === "stalp"
        ? matStalp
        : tip === "perete_beton"
          ? matPereteBeton
          : tip === "zidarie_confinata"
            ? matZc
          : tip === "perete_zidarie"
            ? matPereti
            : tip === "panou"
              ? matPlaca
              : tip === "fundatie" || tip === "radier"
                ? matFundatie
                : tip === "fereastra"
                  ? matFereastra
                  : tip === "usa"
                    ? matUsa
                    : tip === "parapet"
                      ? matParapet
                      : matGrinda;
    // lista, nu un singur mesh: un ax de fundație e desenat din mai multe
    // piese (talpa + elevația fiecărui tronson), iar selecția e AXUL
    let prev: THREE.Mesh[] = [];

    // --- COTELE PIESEI ALESE -------------------------------------------------
    // La fiecare selecție, piesa își scrie singură măsurile: secțiunea și
    // lungimea ei, plus lumina până la piesele din jur (v. `coteObiect3d`).
    // Nu e un layer cu bifă — e ce răspunde la click, ca aurul selecției, și
    // se stinge odată cu ea.
    //
    // Piesele PRISMATICE: barele, peretele de beton și zidul. Toate au fețe
    // plane între care se poate ridica o perpendiculară, deci toate răspund
    // la «cât e de mare?» și «cât e până la ce e în fața ei?». Placa nu:
    // ochiul ei are contur, iar cutia care-l cuprinde n-ar spune nimic
    // adevărat.
    const CU_COTE = new Set([
      "stalp", "grinda", "fundatie", "perete_beton", "perete_zidarie",
      "zidarie_confinata", "radier",
    ]);
    // DOUĂ LUMI, care nu se măsoară una la alta.
    //
    // Betonul se cotează până la beton: stâlpi, grinzi, fundații, pereți de
    // beton, plus plăcile — pe verticală placa e chiar piesa din față (sub o
    // grindă, înălțimea liberă e până la planșeul de dedesubt), iar în plan e
    // mereu în spatele barelor, deci nu adaugă nimic. Zidăria e ÎNVELIȘ, și
    // stâlpul stă CHIAR în ea: o cotă până la zidul în care e turnat ar
    // măsura o lumină care nu există.
    //
    // Zidul se cotează până la zid — lumina camerei, grosimea golului dintre
    // doi pereți —, nu până la stâlpul din el.
    const VECINE_BETON = new Set([
      "stalp", "grinda", "fundatie", "perete_beton", "panou", "placa_pe_sol",
      "zidarie_confinata", "radier",
    ]);
    const VECINE_ZIDARIE = new Set(["perete_zidarie"]);
    const lumea = (tip: string) =>
      tip === "perete_zidarie" ? VECINE_ZIDARIE : VECINE_BETON;
    const gCoteSel = new THREE.Group();
    scene.add(gCoteSel);
    const coteazaSelectia = (alese: THREE.Mesh[]) => {
      elibereazaCote(gCoteSel);
      const tip = (alese[0]?.userData as { tip?: string } | undefined)?.tip;
      if (!tip || !CU_COTE.has(tip)) return;
      const familii = lumea(tip);
      // vecina TĂIATĂ nu se mai măsoară, ca și cea debifată: o cotă întinsă
      // spre un zid pe care secțiunea tocmai l-a aruncat ar arăta o lumină
      // pe care ecranul n-o mai arată.
      const vecini = pickables.filter(
        (m) => !alese.includes(m)
          && familii.has((m.userData as { tip: string }).tip)
          && vizibil(m)
          && (!planCurent || seVedeDupaTaiere(m, planCurent)));
      gCoteSel.add(grupCoteObiect(
        alese, vecini,
        (alese[0].userData as { unghi?: number }).unghi ?? 0));
      // stinsă odată cu familia ei: o cotă rămasă în aer după ce ai debifat
      // stâlpii ar arăta ca o măsură a nimicului
      gCoteSel.visible = vizibil(alese[0]);
    };

    // --- EVIDENȚIEREA pe TIP -------------------------------------------------
    // Oțelul își schimbă culorile per instanță (`GrupArmatura.evidentiaza`);
    // betonul își schimbă materialul, piesă cu piesă, după CLASA elementului
    // din rezumat. Piesa selectată prin click rămâne a selecției: aurul e
    // același, dar ea are un panou deschis, deci nu se atinge.
    let evCurent: Evidentiere = null;
    let evBeton: THREE.Mesh[] = [];
    const aplicaEvBeton = () => {
      evBeton.forEach((m) => {
        if (!prev.includes(m)) {
          m.material = matDupaTip((m.userData as { tip: string }).tip);
        }
      });
      evBeton = [];
      const ev = evCurent;
      const rez = rezArmatura;
      if (!ev || !rez) return;
      if (ev.cat !== "stalpi" && ev.cat !== "grinzi" && ev.cat !== "fundatie") {
        return;
      }
      // clasa piesei, sau `undefined` când piesa nu e din familia cerută
      const clasaLui = (m: THREE.Mesh): string | null | undefined => {
        const u = m.userData as { tip: string; ref: number; ax?: string };
        if (ev.cat === "stalpi" && u.tip === "stalp") {
          return rez.stalpi[model.stalpi[u.ref]?.id_plan ?? ""]?.clasa ?? null;
        }
        if (ev.cat === "grinzi" && u.tip === "grinda") {
          return rez.grinzi[model.grinzi[u.ref]?.id_plan ?? ""]?.clasa ?? null;
        }
        if (ev.cat === "fundatie" && u.tip === "fundatie") {
          return rez.fundatie[u.ax ?? ""]?.clasa ?? null;
        }
        return undefined;
      };
      evBeton = pickables.filter((m) => {
        const c = clasaLui(m);
        return c !== undefined && (ev.tip == null || c === ev.tip);
      });
      evBeton.forEach((m) => { if (!prev.includes(m)) m.material = matSel; });
    };

    // --- SECȚIUNEA ----------------------------------------------------------
    // CE SE UMPLE LA TĂIETURĂ: volumele închise, adică materia care chiar e
    // turnată sau zidită. NU ferestrele și ușile — ele sunt GOLURI, desenate
    // ca niște cutii în grosimea zidului; umplute cu culoarea betonului,
    // tăietura ar arăta zid tocmai acolo unde nu e. Și nu terenul, care nu e
    // un corp închis (v. antetul lui `sectiune3d.ts`).
    const volumeSectiune = [gStalpi, gGrinzi, gPlaca, gPereti, gPeretiBeton,
                            gBalcoane, gFundatie, gPlacaSol, gParapete];
    const limGrila = limiteGrila(volumeSectiune, model.theta_rad ?? 0);
    setLimite(limGrila);
    const sectiune = creeazaSectiune({
      scene, renderer, theta: model.theta_rad ?? 0,
      volume: volumeSectiune, limite: limGrila,
    });

    api.current = {
      render,
      /** Armătura vine ASINCRON: scena se desenează întâi cu betonul, iar
       *  barele se adaugă când sosește rezumatul (sub 30 KB — v.
       *  `armare_3d.py`). `null` o scoate și îi eliberează geometriile;
       *  fără asta, stingerea rândului ar lăsa în memorie zeci de mii de
       *  vârfuri pentru nimic. */
      setArmatura: (rez) => {
        gArmatura.clear();
        armaturaCladita?.dispose();
        armaturaCladita = null;
        rezArmatura = rez;
        if (rez && realist) {
          const a = construiesteArmatura(r, rez,
            { grinzi: grinziFund, fnd });
          gArmatura.add(a.grup);
          armaturaCladita = a;
          a.evidentiaza(evCurent);
          lumini?.ceruUmbra();
        }
        aplicaEvBeton();
        render();
      },
      /** Evidențierea pe TIP de componentă: oțelul per instanță, betonul
       *  per piesă. `null` readuce scena la culorile ei. */
      evidentiaza: (ev) => {
        evCurent = ev;
        armaturaCladita?.evidentiaza(ev);
        aplicaEvBeton();
        // barele evidențiate se ÎNGROAȘĂ, deci umbra lor nu mai e cea
        // calculată la aprinderea armăturii (harta se face o dată)
        lumini?.ceruUmbra();
        render();
      },
      highlight: (s: Selectie) => {
        prev.forEach((m) => {
          m.material = matDupaTip((m.userData as { tip: string }).tip);
          if (m.userData.ordineInainte !== undefined) {
            m.renderOrder = m.userData.ordineInainte as number;
            delete m.userData.ordineInainte;
          }
        });
        prev = [];
        if (s) {
          let alese: THREE.Mesh[] = [];
          if (s.tip === "fundatie") {
            const ax = pickables.find(
              (m) => m.userData.tip === "fundatie" && m.userData.ref === s.ref
            )?.userData.ax;
            alese = pickables.filter(
              (m) => m.userData.tip === "fundatie" && m.userData.ax === ax
            );
          } else {
            alese = pickables.filter(
              (m) => m.userData.tip === s.tip && m.userData.ref === s.ref
            );
          }
          alese.forEach((m) => {
            m.material = s.tip === "panou" ? matPanouSel : matSel;
            if (s.tip === "panou") {
              // ochiul ales se desenează DUPĂ balcoane și terase: altfel
              // placa lor, pusă peste el, îi acoperă jumătate din aur
              m.userData.ordineInainte = m.renderOrder;
              m.renderOrder = (Math.max(...Object.values(ORD)) + 1) * 100000;
            }
          });
          prev = alese;
        }
        coteazaSelectia(prev);
        // piesele scoase din selecție se întorc la evidențierea lor, dacă au
        aplicaEvBeton();
        render();
      },
      setVis: (key: string, on: boolean) => {
        if (key === "placa") gPlaca.visible = on;
        if (key === "mesh") gMesh.visible = on;
        if (key === "noduri") gNoduri.visible = on;
        if (key === "cm") gCm.visible = on;
        if (key === "pereti") gPereti.visible = on;
        if (key === "peretiBeton") gPeretiBeton.visible = on;
        if (key === "zidarieConfinata") gZidarieConfinata.visible = on;
        if (key === "balcoane") gBalcoane.visible = on;
        if (key === "fundatie") gFundatie.visible = on;
        if (key === "placaSol") gPlacaSol.visible = on;
        if (key === "ferestre") gFerestre.visible = on;
        if (key === "usi") gUsi.visible = on;
        if (key === "parapete") gParapete.visible = on;
        if (key === "nuclee") gNuclee.visible = on;
        if (key === "stalpi") gStalpi.visible = on;
        if (key === "grinzi") gGrinzi.visible = on;
        if (key === "cote") {
          if (on) {
            construiesteCote();
            if (!incadrat) { incadrat = true; incadreaza(gCote); }
          }
          gCote.visible = on;
        }
        // Cotele piesei alese se REFAC, nu doar se sting: ele măsoară până
        // la ce se vede, iar o cotă rămasă întinsă spre o grindă tocmai
        // debifată ar arăta o lumină pe care ecranul n-o mai arată.
        if (prev.length) coteazaSelectia(prev);
        if (key === "terenSapatura") {
          if (on) {
            construiesteTeren();
            if (!incadrat) { incadrat = true; incadreaza(gTerenSapatura); }
          }
          gTerenSapatura.visible = on;
        }
        render();
      },
      /** Harta de umbră a layerului realist se calculează O DATĂ
       *  (`autoUpdate = false`), iar jumătatea tăiată nu mai are voie să
       *  arunce umbră: de asta se cere o reîmprospătare la fiecare mutare a
       *  planului, ca la aprinderea armăturii. */
      setSectiune: (s) => {
        sectiune.aseaza(s);
        planCurent = s ? planTaiere(s, model.theta_rad ?? 0) : null;
        // PIESA ÎNGHIȚITĂ DE TĂIETURĂ SE DESELECTEAZĂ. Aurul ei e tăiat ca
        // tot restul, deci pe ecran nu mai rămâne nimic din ea — dar panoul
        // de proprietăți stă deschis pe o piesă care nu se mai vede, iar
        // cotele ei se întind prin aer. Cât timp a rămas ceva din ea
        // (tăiată pe jumătate), selecția e tot bună și rămâne.
        if (prev.length && planCurent
            && !prev.some((m) => seVedeDupaTaiere(m, planCurent!))) {
          onSel(null);              // React întoarce `highlight(null)`
        } else if (prev.length) {
          coteazaSelectia(prev);    // cotele se refac: vecinii tăiați ies
        }
        lumini?.ceruUmbra();
        render();
      },
    };

    // starea curentă a layerelor / selecției, pe scena nou construită
    Object.entries(visRef.current).forEach(([k, on]) => api.current!.setVis(k, on));
    if (selRef.current) api.current.highlight(selRef.current);
    if (evidRef.current) api.current.evidentiaza(evidRef.current);
    if (sectRef.current) api.current.setSectiune(sectRef.current);

    // --- comenzile de navigare (rotiță, trackpad, taste) --------------------
    // TASTA 0 readuce vederea de unde a plecat: aceeași direcție de privire ca
    // la deschidere, cu cadrul strâns pe CE E VIZIBIL acum. Nu pe scena
    // întreagă: cotele și terenul ies mult în afara clădirii, iar cine le-a
    // stins n-are de ce să primească înapoi cadrul lor.
    const reseteaza = () => {
      const cutie = cutiaFizica(scene);
      if (cutie.isEmpty()) return;
      const mij = cutie.getCenter(new THREE.Vector3());
      const raza = Math.max(cutie.getSize(new THREE.Vector3()).length(), 1);
      camera.up.set(0, 0, 1);
      controls.target.copy(mij);
      camera.position.set(mij.x + raza * 0.9, mij.y - raza * 1.1,
                          mij.z + raza * 0.75);
      incadreazaCutia(camera, controls.target, cutie, 0.03);
      controls.update();
    };
    // Scena randează LA CERERE, nu într-o buclă continuă: după fiecare comandă
    // trebuie randat explicit. `controls.update()` randează singur când chiar
    // a mișcat camera (prin evenimentul «change»), deci se randează o dată.
    const desfaComenzi = legComenzi3D(
      renderer.domElement,
      primitiveOrbita(camera, controls, renderer.domElement, {
        reseteaza,
        dupa: () => { if (!controls.update()) render(); },
      }),
    );

    const resize = () => {
      // fallback dacă tabul/containerul e (încă) invizibil la montare —
      // altfel bufferul WebGL rămâne 1px până la primul eveniment de resize
      const w = el.clientWidth || 900;
      const h = el.clientHeight || 560;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      render();
    };
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    window.addEventListener("resize", resize);
    resize();

    return () => {
      ro.disconnect();
      desfaComenzi();
      window.removeEventListener("resize", resize);
      controls.removeEventListener("change", tineMinteCamera);
      controls.dispose();
      renderer.domElement.removeEventListener("pointerdown", onDown);
      renderer.domElement.removeEventListener("pointerup", onUp);
      renderer.domElement.removeEventListener("dblclick", onDbl);
      // CE SE ELIBEREAZĂ, ȘI DE CE TOT. O scenă de clădire ține zeci de MB
      // pe placă: geometrii, materiale, texturi, harta de umbră, mediul de
      // reflexie. Nimic din ele nu pleacă la garbage collector — sunt
      // resurse ale driverului, eliberate numai prin `dispose`. Iar
      // `forceContextLoss` întoarce contextul WebGL: un browser ține
      // 8–16 contexte în viață, deci fără el intrarea și ieșirea din Studio
      // de câteva ori lasă scena următoare fără context.
      armaturaCladita?.dispose();
      elibereazaCote(gCoteSel);
      sectiune.dispose();
      deAruncat.forEach((o) => o.dispose());
      lumini?.dispose();
      mediu?.dispose();
      scene.environment = null;
      fundalTex?.dispose();
      const aruncate = new Set<THREE.Material>();
      scene.traverse((o) => {
        const m = o as THREE.Mesh;
        m.geometry?.dispose();
        (m.userData?.geoCote as THREE.BufferGeometry | undefined)?.dispose();
        const mat = (o as THREE.Mesh).material;
        (Array.isArray(mat) ? mat : mat ? [mat] : []).forEach((x) => {
          if (aruncate.has(x)) return;
          aruncate.add(x);
          x.dispose();
        });
      });
      scene.clear();
      renderer.dispose();
      renderer.forceContextLoss();
      el.removeChild(renderer.domElement);
      api.current = null;
    };
    // scena se reconstruiește când se schimbă rezultatul SAU layerul —
    // materialele, luminile și muchiile sunt altele, iar o reconstrucție
    // curată e mai ieftină decât o schimbare de material pe două sute de
    // obiecte. Camera trece dincolo prin `camRef`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r, layer]);

  /** Armătura se cere NUMAI din layerul realist, și numai când rândul ei e
   *  aprins: în «Clasic» rețeaua nu se atinge deloc. */
  useEffect(() => {
    if (layer !== "realist" || !vis.armatura || !projectId) return;
    let viu = true;
    getArmatura3d(projectId, cloneId, versiune)
      .then((a) => { if (viu) setArmatura(a); })
      .catch(() => { /* pașii de armare n-au rulat: se desenează betonul */ });
    return () => { viu = false; };
  }, [layer, vis.armatura, projectId, cloneId, versiune]);

  useEffect(() => {
    api.current?.setArmatura(vis.armatura ? armatura : null);
  }, [armatura, vis.armatura, layer]);

  useEffect(() => {
    api.current?.highlight(sel);
  }, [sel]);

  useEffect(() => {
    api.current?.evidentiaza(evid);
  }, [evid]);

  useEffect(() => {
    api.current?.setVis("placa", vis.placa);
    api.current?.setVis("mesh", vis.mesh);
    api.current?.setVis("noduri", vis.noduri);
    api.current?.setVis("cm", vis.cm);
    api.current?.setVis("pereti", vis.pereti);
    api.current?.setVis("peretiBeton", vis.peretiBeton);
    api.current?.setVis("zidarieConfinata", vis.zidarieConfinata);
    api.current?.setVis("balcoane", vis.balcoane);
    api.current?.setVis("fundatie", vis.fundatie);
    api.current?.setVis("placaSol", vis.placaSol);
    api.current?.setVis("ferestre", vis.ferestre);
    api.current?.setVis("usi", vis.usi);
    api.current?.setVis("parapete", vis.parapete);
    api.current?.setVis("nuclee", vis.nuclee);
    api.current?.setVis("terenSapatura", vis.terenSapatura);
    api.current?.setVis("cote", vis.cote);
    api.current?.setVis("stalpi", vis.stalpi);
    api.current?.setVis("grinzi", vis.grinzi);
  }, [vis]);

  useEffect(() => {
    api.current?.setSectiune(sect);
  }, [sect]);

  /** Un rezultat nou (rerularea pasului) poate veni cu altă clădire, deci cu
   *  alte capete de cursor: planul rămas de la clădirea dinainte s-ar putea
   *  trezi în afara ei — tăietură aprinsă, dar nimic tăiat. Se readuce între
   *  capete; când n-a ieșit din ele, nu se atinge nimic. */
  useEffect(() => {
    if (!limite) return;
    setSect((s) => {
      if (!s) return s;
      const t = Math.min(Math.max(s.t, limite[s.axa].min), limite[s.axa].max);
      return t === s.t ? s : { ...s, t };
    });
  }, [limite]);

  /** Cotele de care se prinde tăietura orizontală: MIJLOCUL fiecărui nivel,
   *  nu cota plăcii lui (v. antetul lui `BaraSectiune`). Înălțimea vine de
   *  la etaj când o are; altfel se deduce din nivelul de dedesubt, iar la
   *  primul din media clădirii. */
  const niveluriSectiune = r.model.etaje.map((et, i) => {
    const jos = et.inaltime_m != null ? et.z_m - et.inaltime_m
      : i > 0 ? r.model.etaje[i - 1].z_m
        : et.z_m - (r.model.inaltime_etaj_m || 3);
    return { nivel: et.nivel, z_m: (jos + et.z_m) / 2 };
  });

  /** Ce jumătate se ARUNCĂ la aprindere: cea dinspre om. Altfel jumătate din
   *  aprinderi ar tăia pe dos, iar omul ar vedea spatele clădirii în loc de
   *  interiorul ei — și n-ar avea cum ghici că-i lipsește o apăsare pe ⇄. */
  const inversImplicit = (axa: AxaSectiune) => {
    const c = camRef.current;
    const spre = c ? c.poz.clone().sub(c.tinta) : new THREE.Vector3(1, -1, 1);
    return inversPentruCamera(axa, r.model.theta_rad ?? 0, spre);
  };

  // Panoul de vizibilitate e și LEGENDA scenei: fiecare rând poartă pastila
  // de culoare cu care e desenat elementul, deci nu mai trebuie o legendă
  // separată ca să știi ce vezi. ORICE layer se poate stinge, stâlpii și
  // grinzile inclusiv: fără ele nu mai e model, dar tocmai ele stau în fața a
  // ce e dedesubt. «doar» pe un rând lasă numai layerul ăla aprins — ca să
  // te uiți la un singur lucru —, iar «toate» / «nimic» pornesc de la capăt.
  // PASTILA E LEGENDA, deci urmează layerul: în «Realist» betonul e unul
  // singur, de sticlă, iar o pastilă albastră ar promite un albastru care nu
  // mai apare nicăieri pe ecran.
  const sticla = "linear-gradient(180deg,#c8cfd6,#9aa6b1)";
  const pastila = (clasic: string, realist = sticla) =>
    (layer === "realist" ? realist : clasic);
  const toggles: [keyof typeof vis, string, string][] = [
    ["stalpi", t("struct.fem.lay.stalpi"), pastila(CULORI_CSS.stalp)],
    ["grinzi", t("struct.fem.lay.grinzi"), pastila(CULORI_CSS.grinda)],
    // placa e PORNITĂ implicit: e subiectul paginii
    ["placa", t("struct.fem.lay.placa"),
     pastila(CULORI_CSS.placa, "linear-gradient(180deg,#d8dee4,#aab4be)")],
  ];
  if (layer === "realist") {
    const n = armatura?.are;
    const cate = n
      ? n.stalpi + n.grinzi + n.pereti + n.placa + (n.fundatie ?? 0) : 0;
    toggles.push(["armatura",
      cate ? t("struct.fem.lay.armatura_n", { n: cate })
        : armatura ? t("struct.fem.lay.armatura_nerulat")
                   : t("struct.fem.lay.armatura"),
      "#2f343a"]);
  }
  if (r.model.shells?.length)
    toggles.push(["mesh", t("struct.fem.lay.mesh"), CULORI_CSS.placa]);
  toggles.push(["noduri", t("struct.fem.lay.noduri"), CULORI_CSS.nodFem]);
  if (!r.model.shells?.length) {
    toggles.push(["cm", t("struct.fem.lay.cm"), CULORI_CSS.masa]);
  }
  if (r.model.etaje.some((et) => et.pereti?.length))
    toggles.push(["pereti", t("struct.fem.lay.pereti"),
      pastila(CULORI_CSS.logie, "linear-gradient(180deg,#d3d8dd,#aab2ba)")]);
  if (r.model.pereti_beton?.length)
    toggles.push(["peretiBeton", t("struct.fem.lay.pereti_beton"),
      pastila(CULORI_CSS.pereteBeton)]);
  // pastila zidăriei confinate e peretele CU stâlpișorii lui la capete:
  // ambele culori apar în scenă și pastila le spune pe amândouă
  if (r.model.pereti_zidarie?.length)
    toggles.push(["zidarieConfinata", t("struct.fem.lay.zidarie_confinata"),
      pastila(`linear-gradient(90deg,${CULORI_CSS.stalpisorZc} 0 24%,`
        + `${CULORI_CSS.zidarieConfinata} 24% 76%,`
        + `${CULORI_CSS.stalpisorZc} 76%)`)]);
  if (r.model.etaje.some((et) => et.balcoane?.length))
    toggles.push(["balcoane", t("struct.fem.lay.balcoane"),
      pastila(CULORI_CSS.balconBeton,
        "linear-gradient(180deg,#d8dee4,#aab4be)")]);
  const { fnd, inModel: areFundatie, grinzi: grinziFundDesen } =
    fundareaDesenata(r.model);
  const radiereLay = radiereDesenate(r.model);
  if (grinziFundDesen.length || radiereLay.length)
    toggles.push(["fundatie",
                  t(radiereLay.length ? "struct.fem.lay.radier"
                    : areFundatie ? "struct.fem.lay.fundatie"
                                  : "struct.fem.lay.fundatie_afara"),
                  CULORI_CSS.fundatie]);
  const psol = placaPeSolDesenata(r.model);
  if (psol?.contur?.length)
    toggles.push(["placaSol", t("struct.fem.lay.placa_sol"),
                  CULORI_CSS.zidarie]);
  // ARHITECTURA importată, la coada listei și stinsă implicit: golurile și
  // parapetele nu intră în niciun calcul, dar sunt singura cale de a verifica
  // în 3D ce a citit Pasul 2 de pe planuri. Culorile sunt cele ale layerelor
  // generate ale planului (`PAL_LAYERE`), ca o fereastră să arate la fel în
  // plan și în scenă.
  const nrFer = goluriFlat(r.model, "fereastra").length;
  const nrUsi = goluriFlat(r.model, "usa").length;
  const nrPar = parapeteFlat(r.model).length;
  if (nrFer) {
    toggles.push(["ferestre", t("struct.fem.lay.ferestre", { n: nrFer }),
                  PAL_LAYERE.ferestre]);
  }
  if (nrUsi) {
    toggles.push(["usi", t("struct.fem.lay.usi", { n: nrUsi }), PAL_LAYERE.usi]);
  }
  if (nrPar) {
    toggles.push(["parapete", t("struct.fem.lay.parapete", { n: nrPar }),
                  PAL_LAYERE.margine]);
  }
  // NUCLEUL (casele de scară, lifturile): tot din arhitectură, dar aprins —
  // e reperul după care structuristul își așază pereții mari
  const nrNuc = nucleeFlat(r.model).length;
  if (nrNuc) {
    toggles.push(["nuclee", t("struct.fem.lay.nuclee", { n: nrNuc }),
                  `linear-gradient(90deg, ${CULORI_CSS.scara} 50%,`
                  + ` ${CULORI_CSS.lift} 50%)`]);
  }
  // TERENUL și SĂPĂTURA, la coada listei: un singur rând pentru amândouă,
  // fiindcă una e pământul și cealaltă e golul din el. Pastila e în două
  // culori — cafeniul stratului și portocaliul săpăturii.
  toggles.push(["cote", t("struct.fem.lay.cote"), CULORI_CSS.acc]);
  const sap = r.model.sapatura;
  if (sap)
    toggles.push(["terenSapatura", t("struct.fem.lay.teren"),
                  `linear-gradient(90deg, ${PAL_TEREN.coeziv_mare.culoare} 50%,`
                  + ` ${PAL_SAPATURA} 50%)`]);
  const titluPlacaSol = psol
    ? t("struct.fem.psol_titlu", {
        nivel: psol.nivel
          ? t("struct.fem.psol_nivel", { nivel: psol.nivel }) : "",
        arie: psol.arie_m2 ?? "—",
        grosime: (psol.grosime_m * 100).toFixed(0),
        clasa: psol.clasa ? t("struct.fem.psol_clasa", { clasa: psol.clasa }) : "",
        balast: psol.balast_m
          ? t("struct.fem.psol_balast",
              { cm: (psol.balast_m * 100).toFixed(0) }) : "",
      })
    : undefined;
  const titluFundatie = radiereLay.length
    ? t("struct.fem.rad_lay_titlu", {
        n: radiereLay.length,
        cm: (Math.max(...radiereLay.map((x) => x.h_m)) * 100).toFixed(0),
        arie: nr(radiereLay.reduce((a, x) => a + (x.amprenta_m2 ?? 0), 0), 0),
      })
    : t("struct.fem.fnd_titlu", {
        detaliu: fnd.detaliu_descriere ?? t("struct.fem.fnd_sectiune"),
        cota: fnd.Df_m ? t("struct.fem.fnd_cota", { m: nr(fnd.Df_m, 2) }) : "",
      });

  // În meniu: la vedere tot ce e aprins la deschidere — structura, zidăria,
  // balcoanele, placa pe sol —, ca fiecare lucru desenat pe ecran să aibă
  // comutatorul la îndemână. Sub «Mai multe straturi» rămân doar cele stinse
  // din start: notația de calcul, arhitectura, cotele, terenul. Împărțirea
  // urmează starea de la deschidere, nu pe cea de acum: un strat stins nu
  // sare din listă în listă sub mâna omului.
  const aprinsLaStart = visImplicit();
  const principale = toggles.filter(([k]) => aprinsLaStart[k]);
  const celelalte = toggles.filter((x) => !principale.includes(x));
  const comutatorStrat = ([key, label, culoare]: typeof toggles[number]) => (
    <button key={key} type="button"
            className={`meniu-comutator${vis[key] ? " on" : ""}`}
            aria-pressed={vis[key]}
            onClick={() => setVis({ ...vis, [key]: !vis[key] })}>
      <span className="fem-pastila" style={{ background: culoare }} />
      <span>{label}</span>
    </button>
  );
  const inMeniu = straturiIn && createPortal(
    <>
      <div className="meniu-comutatoare">{principale.map(comutatorStrat)}</div>
      {celelalte.length > 0 && (
        <details className="meniu-mai-mult">
          <summary>
            <span>{t("struct.fem.mai_multe_straturi")}</span>
            <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
              <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor"
                    strokeWidth="1.5" strokeLinecap="round"
                    strokeLinejoin="round" />
            </svg>
          </summary>
          <div className="meniu-comutatoare">{celelalte.map(comutatorStrat)}</div>
          <span className="meniu-actiuni">
            <button type="button" onClick={() => setToate(true)}
                    title={t("struct.fem.lay_toate_titlu")}>
              {t("struct.fem.lay_toate")}</button>
            <button type="button" onClick={() => setToate(false)}
                    title={t("struct.fem.lay_nimic_titlu")}>
              {t("struct.fem.lay_nimic")}</button>
          </span>
          {layer === "realist" && armatura?.tipuri && vis.armatura && (
            <Evidentiere3D tipuri={armatura.tipuri} valoare={evid}
                           onChange={setEvid} />
          )}
        </details>
      )}
    </>,
    straturiIn);

  return (
    <div className="fem-scene">
      {inMeniu}
      <BaraSectiune
        stare={sect}
        onStare={setSect}
        limite={limite}
        niveluri={niveluriSectiune}
        inversImplicit={inversImplicit}
      />
      <Layere3D
        layer={layer}
        onLayer={setLayer}
        nota={layer === "realist" && armatura && !armatura.are.stalpi
          && !armatura.are.grinzi ? t("struct.fem.fara_armatura") : null}
      />
      {!straturiIn && <div className="fem-toggles">
        <div className="fem-toggles-cap">
          <span className="ob-lbl">{t("struct.fem.ce_se_vede")}</span>
          <span className="fem-toggles-actiuni">
            <button type="button" onClick={() => setToate(true)}
                    title={t("struct.fem.lay_toate_titlu")}>
              {t("struct.fem.lay_toate")}</button>
            <button type="button" onClick={() => setToate(false)}
                    title={t("struct.fem.lay_nimic_titlu")}>
              {t("struct.fem.lay_nimic")}</button>
          </span>
        </div>
        {toggles.map(([key, label, culoare]) => (
          <label
            key={key}
            title={
              key === "fundatie"
                ? titluFundatie
                : key === "noduri"
                    ? t("struct.fem.help_noduri")
                    : key === "placaSol"
                      ? titluPlacaSol
                      : key === "terenSapatura" && sap
                        ? titluTerenSapatura(sap)
                        : key === "cote"
                          ? t("struct.fem.help_cote")
                          : key === "stalpi"
                            ? t("struct.fem.help_stalpi")
                            : key === "grinzi"
                              ? t("struct.fem.help_grinzi")
                              : undefined
            }
          >
            <input
              type="checkbox"
              checked={vis[key]}
              onChange={(e) => setVis({ ...vis, [key]: e.target.checked })}
            />
            <span className="fem-pastila" style={{ background: culoare }} />
            {label}
            <button
              type="button"
              className="fem-doar"
              title={t("struct.fem.lay_doar_titlu")}
              onClick={(e) => { e.preventDefault(); setDoar(key); }}
            >
              {t("struct.fem.lay_doar")}
            </button>
          </label>
        ))}
        {layer === "realist" && armatura?.tipuri && vis.armatura && (
          <Evidentiere3D tipuri={armatura.tipuri} valoare={evid}
                         onChange={setEvid} />
        )}
        {/* Baza încastrată rămâne SCRISĂ — e o ipoteză de calcul, nu un
            amănunt. Betonul însă se desenează: el există sub clădire oricum,
            doar că nu participă la calcul. Layerul lui e stins implicit
            . */}
        {!areFundatie && (
          <span
            className="fem-fix"
            title={
              (r.fundatie?.motiv ?? t("struct.fem.baza_motiv"))
              + (grinziFundDesen.length ? t("struct.fem.baza_beton") : "")
            }
          >
            <span className="fem-pastila" style={{ background: CULORI_CSS.ink }} />
            {t("struct.fem.baza_incastrata")}
          </span>
        )}
      </div>}
      <div ref={mount} className="fem-canvas" />
    </div>
  );
}
